import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, Search, X, ChevronDown, ChevronUp, Trash2, PackageCheck,
  Receipt, ShoppingBag, Paperclip, Send, SlidersHorizontal,
  Inbox, ChevronRight, Save, Loader2,
  Package, Wrench, Tag, AlignLeft, Layers,
  RotateCcw, Clock, FolderOpen, FileText, GitBranch, RefreshCw,
  Mail, Download, GripVertical, CreditCard, UploadCloud, ExternalLink
} from 'lucide-react';

const compte_charge_missing = (lignes) =>
  lignes.some(l => l.article_id && !l.compte_charge);
import { toast } from 'sonner';
import * as api from '../api';
import EcheancesTab from '../components/EcheancesTab';
import { checkSiren } from '../utils/checkSiren';
import { SmartButton } from '../components/SmartButton';
import { ActivitesFichePanel, useHasActivite, ActivityPulse } from '../components/ActivitesFichePanel';import { ModalNouvelleDiscussion } from '../components/ModalNouvelleDiscussion';

import { useSociete } from '../context/SocieteContext';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';
import { pageCache } from '../utils/pageCache';
import { SidePanel } from '../components/SidePanel';
import { NumericInput } from '../components/NumericInput';
import DocumentsTab from '../components/DocumentsTab';

// Styles boutons d'action — fond plein, texte blanc (voir UI_PATTERNS.md)
const BTN = "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-opacity whitespace-nowrap hover:opacity-90";
const BTN_PRIMARY = { background: 'var(--primary-gradient)' };
const BTN_GREEN   = { background: '#15803D' };
const BTN_VIOLET  = { background: '#7C3AED' };
const BTN_EMERALD = { background: '#059669' };
const BTN_RED     = { background: '#DC2626' };

const ACHAT_COLUMN_DEFS = [
  { key: 'numero',      label: 'Numéro',           locked: true  },
  { key: 'fournisseur', label: 'Fournisseur',       locked: false },
  { key: 'titre',       label: 'Titre',             locked: false },
  { key: 'date',        label: 'Date commande',     locked: false },
  { key: 'livraison',   label: 'Livraison prévue',  locked: false },
  { key: 'nb_lignes',   label: 'Articles',          locked: false },
  { key: 'total_ttc',   label: 'Total TTC',         locked: false },
  { key: 'stade',       label: 'Statut',            locked: true  },
  { key: 'facturation', label: 'Facturation',       locked: false },
  { key: 'reception',   label: 'Réception',         locked: false },
];

// Poids de chaque colonne (somme = 100 quand toutes visibles)
const COL_WEIGHTS = {
  _checkbox:   2,
  numero:      11,
  fournisseur: 17,
  titre:       13,
  date:        7,
  livraison:   7,
  nb_lignes:   4,
  total_ttc:   8,
  stade:       8,
  facturation: 8,
  reception:   8,
  _suivi_par:  6,
  _spacer:     1,
};
const ACHAT_DEFAULT_COLS = Object.fromEntries(ACHAT_COLUMN_DEFS.map(c => [c.key, true]));
const ACHAT_COLS_KEY = 'achats_visible_cols_v2';
function loadAchatCols() {
  try {
    const saved = JSON.parse(localStorage.getItem(ACHAT_COLS_KEY) || 'null');
    if (saved && typeof saved === 'object') return { ...ACHAT_DEFAULT_COLS, ...saved, numero: true, stade: true };
  } catch {}
  return { ...ACHAT_DEFAULT_COLS };
}

// ─── FilterDropdown — identique au pattern Articles.js ───────────────────────

function FilterDropdown({ label, options, selected, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  const n = selected.length;
  const isActive = n > 0;
  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button onClick={() => setOpen(v => !v)} data-testid={testId}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm whitespace-nowrap"
        style={{ borderColor: isActive ? 'var(--primary-solid)' : 'var(--text-disabled)', color: isActive ? 'var(--primary-solid)' : 'var(--text-secondary)', background: isActive ? 'var(--primary-50)' : 'white' }}>
        {label}
        {isActive && <span className="px-1.5 py-0.5 rounded-full text-xs font-bold text-white" style={{ background: 'var(--primary-solid)' }}>{n}</span>}
        <ChevronDown size={13} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border shadow-xl z-40 py-1" style={{ borderColor: 'var(--border)', minWidth: '180px' }}>
          {isActive && (
            <button onClick={() => onChange([])} className="w-full px-3 py-1.5 text-left text-xs flex items-center gap-1.5 hover:bg-gray-50" style={{ color: 'var(--text-muted)' }}>
              <X size={10} /> Tout effacer
            </button>
          )}
          <div className="max-h-60 overflow-y-auto">
            {options.map(opt => (
              <label key={opt.value} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)}
                  className="w-3.5 h-3.5 rounded flex-shrink-0" style={{ accentColor: 'var(--primary-solid)' }} />
                {opt.dot && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: opt.dot }} />}
                <span className="text-sm whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const STADES = [
  { value: 'devis',        label: 'Demande de prix',  bg: '#F8FAFC',           color: '#475569' },
  { value: 'devis_envoye', label: 'Demande envoyée',  bg: '#EFF6FF',           color: '#1D4ED8' },
  { value: 'commande',     label: 'Commande',          bg: '#F0FDF4',           color: '#15803D' },
  { value: 'annule',       label: 'Annulé',            bg: '#FEF2F2',           color: '#DC2626' },
];

// Cards compteurs — uniquement le cycle de vie de la commande
const STADES_CARDS = {
  devis:        { label: 'Demande de prix', bg: '#F1F5F9', color: '#475569', border: '#94A3B8' },
  devis_envoye: { label: 'Demande envoyée', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  commande:     { label: 'Commande',        bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
  annule:       { label: 'Annulé',          bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
};

const stadeInfo = (val) => STADES.find(s => s.value === val) || { label: val || '—', bg: '#F1F5F9', color: '#475569' };

function _fmtDatetime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function _evtAchatConfig(type) {
  const cfgs = {
    revision_creee:    { label: 'Révision créée',      color: '#D97706', bg: '#FEF3C7', border: '#FDE68A', Icon: RotateCcw },
    revision_restauree:{ label: 'Version rappelée',    color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE', Icon: RotateCcw },
    modification:      { label: 'Bon d\'achat modifié',color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', Icon: RefreshCw },
    creation:          { label: 'Bon d\'achat créé',   color: '#059669', bg: '#F0FDF4', border: '#BBF7D0', Icon: FileText },
    stade_change:      { label: 'Stade modifié',       color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE', Icon: GitBranch },
  };
  return cfgs[type] || { label: type, color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB', Icon: Clock };
}

/**
 * Résout le taux TVA à appliquer pour un article à l'achat.
 * Priorité : tva_achat_type_id de l'article → correspondance dans les taux resolus → premier taux disponible → 20 par défaut.
 */
function _resolveTauxTva(tvaTypeId, tvaRates) {
  if (tvaTypeId && tvaRates.length > 0) {
    const match = tvaRates.find(r => r.tva_type_id === tvaTypeId);
    if (match) return match.taux;
  }
  return tvaRates.length > 0 ? tvaRates[0].taux : 20;
}

const EMPTY_FORM = {
  fournisseur_id: '',
  fournisseur_nom: '',
  contact_id: '',
  contact_nom: '',
  titre: '',
  ref_fournisseur: '',
  conditions_paiement: '',
  date_commande: new Date().toISOString().slice(0, 10),
  date_livraison_prevue: '',
  notes: '',
  lignes: [],
};

const EMPTY_LIGNE = {
  _key: Date.now(),
  article_id: '',
  article_ref: '',
  article_designation: '',
  quantite_commandee: 1,
  prix_unitaire_ht: 0,
  montant_devise: null,
  taux_tva: 20,
  compte_charge: '',
};

// ─── Sous-composants ──────────────────────────────────────────────────────────

function StageBadge({ stade }) {
  const s = stadeInfo(stade);
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

const RECEPTION_CFG = {
  en_cours: { label: 'En cours',  bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
  partiel:  { label: 'Partiel',   bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  recu:     { label: 'Reçu',      bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
};
function ReceptionBadge({ statut }) {
  const cfg = RECEPTION_CFG[statut];
  if (!cfg) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`, borderRadius: 100, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

const FACTURATION_ACHAT_CFG = {
  a_deposer:           { label: 'À déposer',   bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
  en_attente_paiement: { label: 'À valider',  bg: '#FFFBEB', color: '#B45309', border: '#FCD34D' },
  validee:             { label: 'Facturée',    bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  payee:               { label: 'Payée',       bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
};
function FacturationAchatBadge({ statut }) {
  const cfg = FACTURATION_ACHAT_CFG[statut];
  if (!cfg) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`, borderRadius: 100, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

function TagBadge({ label, color }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border"
      style={{ background: color + '15', color, borderColor: color + '40' }}>
      {label}
    </span>
  );
}

function TabBadge({ n }) {
  if (!n) return null;
  return (
    <span style={{
      background: 'var(--primary-solid)', color: '#fff',
      borderRadius: 9999, padding: '0 5px', height: 15,
      fontSize: 10, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 15, lineHeight: 1,
    }}>{n}</span>
  );
}

// ─── Modal Confirmation Facture ───────────────────────────────────────────────

function FactureConfirmModal({ open, onClose, onConfirm, sending }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => { if (!v && !sending) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0"
          style={{ background: 'rgba(0,0,0,0.5)', zIndex: 400 }}
        />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
          style={{ transform: 'translate(-50%, -50%)', zIndex: 401, outline: 'none' }}
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
        >
          <div className="px-6 py-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: '#EFF6FF' }}>
                <Send size={20} style={{ color: '#2563EB' }} />
              </div>
              <p className="text-sm pt-2.5" style={{ color: 'var(--text-muted)' }}>
                Vous envoyez cette facture <strong>en comptabilité</strong>, êtes-vous sûr ?
              </p>
            </div>
          </div>
          <div className="flex gap-3 px-6 py-4 border-t justify-end" style={{ borderColor: 'var(--border-light)', background: 'var(--bg-subtle)' }}>
            <button onClick={onClose} disabled={sending}
              className="px-4 py-2 rounded-xl text-sm" style={{ color: 'var(--text-muted)' }}>
              Annuler
            </button>
            <button onClick={onConfirm} disabled={sending}
              data-testid="confirm-odoo-send-btn"
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'var(--primary-gradient)' }}>
              <Send size={14} />
              {sending ? 'Envoi en cours...' : 'Confirmer l\'envoi'}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ─── Couleurs par type d'article ──────────────────────────────────────────────
const TYPE_DOT_COLORS = {
  materiel:       '#4F46E5',
  piece_detachee: '#EA580C',
  prestation:     '#16A34A',
  main_oeuvre:    '#9333EA',
  texte:          '#D97706',
  section:        '#6366F1',
};
const TYPE_REF_COLORS = {
  materiel:       { bg: '#EEF2FF', color: '#4338CA' },
  piece_detachee: { bg: '#FFF7ED', color: '#C2410C' },
  prestation:     { bg: '#F0FDF4', color: '#166534' },
  main_oeuvre:    { bg: '#FDF4FF', color: '#7E22CE' },
};

// ─── TypeSelectDropdown ───────────────────────────────────────────────────────
function TypeSelectDropdown({ value, onChange, opts }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef();
  const current = opts.find(o => o.value === value) || opts[0];
  const dotColor = TYPE_DOT_COLORS[value] || '#9CA3AF';

  const toggle = (e) => {
    e.stopPropagation();
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
    setOpen(o => !o);
  };

  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle}
        className="flex items-center gap-1.5 text-xs border rounded-md px-2 flex-shrink-0 transition-colors"
        style={{ height: 28, borderColor: '#E6E6E1', color: '#6B7280', background: '#fff', whiteSpace: 'nowrap', cursor: 'pointer' }}>
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
        <span>{current?.label || 'Tous types'}</span>
        <ChevronDown size={9} style={{ opacity: 0.6 }} />
      </button>
      {typeof document !== 'undefined' && createPortal(
        <>
          {open && (
            <div data-radix-portal="" style={{ position: 'fixed', inset: 0, zIndex: 10000, cursor: 'default', pointerEvents: 'auto' }}
              onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          )}
          {open && pos && (
            <div data-radix-portal="" style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 10001,
              background: '#fff', border: '1px solid #E6E6E1', borderRadius: 10,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)', overflow: 'hidden', minWidth: 160, pointerEvents: 'auto' }}>
              {opts.map(o => {
                const dc = TYPE_DOT_COLORS[o.value] || '#9CA3AF';
                return (
                  <button key={o.value} type="button"
                    onClick={(e) => { e.stopPropagation(); onChange(o.value); setOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors"
                    style={{ fontWeight: o.value === value ? 600 : 400,
                             background: o.value === value ? '#F8FAFC' : 'transparent',
                             cursor: 'pointer' }}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dc }} />
                    <span style={{ color: '#374151' }}>{o.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </>,
        document.body
      )}
    </>
  );
}

// ─── LigneAchatRow ───────────────────────────────────────────────────────────
function LigneAchatRow({ id, ligne, onChangeLigne, onDeleteLigne, tvaRates = [], readOnly = false, uniteDecimals = {}, isDeviseMode = false, fournisseurDevise = 'USD', tauxChange = null }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const navigate = useNavigate();
  const typeLigne = ligne.type_ligne || 'article';

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 10 : 'auto',
    borderBottom: '1px solid #E6E6E1',
  };

  const dragHandle = !readOnly && (
    <td style={{ width: 28, verticalAlign: 'middle', paddingLeft: 4 }}>
      <span {...attributes} {...listeners}
        style={{ color: '#CBD5E1', cursor: isDragging ? 'grabbing' : 'grab', display: 'inline-flex', alignItems: 'center', touchAction: 'none' }}>
        <GripVertical size={13} />
      </span>
    </td>
  );

  const deleteBtn = !readOnly && (
    <td style={{ width: 36, textAlign: 'center', verticalAlign: 'middle' }}>
      <button type="button" onClick={() => onDeleteLigne(ligne._key)}
        className="p-1 rounded hover:bg-red-50 transition-colors" style={{ color: '#DC2626' }}>
        <Trash2 size={13} />
      </button>
    </td>
  );

  if (typeLigne === 'section') {
    return (
      <tr ref={setNodeRef} style={{ ...rowStyle, background: isDragging ? 'var(--surface)' : '#EEF2FF' }}>
        {dragHandle}
        <td colSpan={readOnly ? (isDeviseMode ? 7 : 6) : (isDeviseMode ? 7 : 6)} className="px-3 py-2 align-middle">
          {readOnly ? (
            <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#3730A3' }}>
              {ligne.designation || <span style={{ color: '#A5B4FC' }}>Section sans titre</span>}
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <Layers size={13} style={{ color: '#6366F1', flexShrink: 0 }} />
              <input value={ligne.designation || ''}
                onChange={e => onChangeLigne(ligne._key, 'designation', e.target.value)}
                placeholder="Titre de la section…"
                className="flex-1 text-xs font-bold bg-transparent focus:outline-none tracking-wide"
                style={{ color: '#3730A3' }}
                autoFocus={ligne.designation === ''} />
            </div>
          )}
        </td>
        {deleteBtn}
      </tr>
    );
  }

  if (typeLigne === 'texte') {
    return (
      <tr ref={setNodeRef} style={{ ...rowStyle, background: isDragging ? 'var(--surface)' : '#FFFBEB' }}>
        {dragHandle}
        <td colSpan={readOnly ? (isDeviseMode ? 7 : 6) : (isDeviseMode ? 7 : 6)} className="px-3 py-2 align-middle">
          {readOnly ? (
            <span className="text-xs italic" style={{ color: '#78350F' }}>{ligne.designation || '—'}</span>
          ) : (
            <div className="flex items-center gap-2">
              <AlignLeft size={13} style={{ color: '#D97706', flexShrink: 0 }} />
              <input value={ligne.designation || ''}
                onChange={e => onChangeLigne(ligne._key, 'designation', e.target.value)}
                placeholder="Saisissez votre texte…"
                className="flex-1 text-xs italic bg-transparent focus:outline-none"
                style={{ color: '#78350F' }}
                autoFocus={ligne.designation === ''} />
            </div>
          )}
        </td>
        {deleteBtn}
      </tr>
    );
  }

  // Ligne article
  const totalLigne = (Number(ligne.quantite_commandee) || 0) * (Number(ligne.prix_unitaire_ht) || 0);

  return (
    <tr ref={setNodeRef} style={rowStyle}>
      {dragHandle}
      <td className="px-2 py-1.5 align-middle" style={{ width: 180, minWidth: 180 }}>
        {ligne.article_ref ? (() => {
          const rc = TYPE_REF_COLORS[ligne.type_article] || { bg: '#EEF2FF', color: '#4338CA' };
          return (
            <button
              type="button"
              onClick={() => navigate(`/articles?open=${ligne.article_id}`)}
              className="font-mono text-xs px-1.5 py-0.5 rounded hover:underline cursor-pointer"
              style={{ background: rc.bg, color: rc.color, whiteSpace: 'nowrap' }}
              title="Ouvrir la fiche article"
            >
              {ligne.article_ref}
            </button>
          );
        })() : (
          <span className="text-xs" style={{ color: '#9CA3AF' }}>—</span>
        )}
      </td>
      <td className="px-2 py-1.5 align-middle">
        {readOnly ? (
          <span className="text-xs" style={{ color: '#111827' }}>{ligne.article_designation || '—'}</span>
        ) : (
          <input
            type="text"
            value={ligne.article_designation || ''}
            onChange={e => onChangeLigne(ligne._key, 'article_designation', e.target.value)}
            placeholder="Désignation…"
            className="text-xs px-1.5 py-0.5 border rounded w-full focus:outline-none focus:ring-1"
            style={{ borderColor: '#E6E6E1', minWidth: 120 }}
          />
        )}
      </td>
      <td className="px-2 py-1.5 align-middle" style={{ width: 70 }}>
        {readOnly ? (
          <span className="text-xs block text-center" style={{ color: '#111827' }}>
            {Number(ligne.quantite_commandee ?? 0).toLocaleString('fr-FR', {
              minimumFractionDigits: ligne.nb_decimales ?? uniteDecimals[ligne.unite_symbole] ?? 0,
              maximumFractionDigits: ligne.nb_decimales ?? uniteDecimals[ligne.unite_symbole] ?? 0
            })}
          </span>
        ) : (
          <NumericInput
            value={ligne.quantite_commandee ?? 1}
            onChange={v => onChangeLigne(ligne._key, 'quantite_commandee', v)}
            min={0}
            decimals={ligne.nb_decimales ?? uniteDecimals[ligne.unite_symbole] ?? 0}
            className="text-xs px-1.5 py-0.5 border rounded w-full text-center focus:outline-none focus:ring-1"
            style={{ borderColor: '#E6E6E1' }}
          />
        )}
      </td>
      {/* Montant devise — colonne conditionnelle */}
      {isDeviseMode && (
        <td className="px-2 py-1.5 align-middle" style={{ width: 110 }}>
          {readOnly ? (
            <span className="text-xs block text-right font-medium" style={{ color: '#7C3AED' }}>
              {ligne.montant_devise != null
                ? (Number(ligne.montant_devise) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '—'}
            </span>
          ) : (
            <NumericInput
              value={ligne.montant_devise ?? ''}
              onChange={v => onChangeLigne(ligne._key, 'montant_devise', v)}
              min={0}
              placeholder="0.00"
              className="text-xs px-1.5 py-0.5 border rounded w-full text-right focus:outline-none focus:ring-1"
              style={{ borderColor: '#C4B5FD', color: '#7C3AED', fontWeight: 500 }}
            />
          )}
        </td>
      )}
      <td className="px-2 py-1.5 align-middle" style={{ width: 100 }}>
        {readOnly ? (
          <span className="text-xs block text-right" style={{ color: '#111827' }}>
            {(Number(ligne.prix_unitaire_ht) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
          </span>
        ) : isDeviseMode ? (
          // En mode devise : P.U. HT est calculé automatiquement → lecture seule avec style grisé
          <span className="text-xs block text-right px-1.5 py-0.5 rounded"
            style={{ color: '#6B7280', background: '#F3F4F6', border: '1px solid #E5E7EB' }}>
            {ligne.prix_unitaire_ht != null && ligne.prix_unitaire_ht !== 0
              ? (Number(ligne.prix_unitaire_ht) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : '—'}
          </span>
        ) : (
          <NumericInput
            value={ligne.prix_unitaire_ht ?? 0}
            onChange={v => onChangeLigne(ligne._key, 'prix_unitaire_ht', v)}
            min={0}
            className="text-xs px-1.5 py-0.5 border rounded w-full text-right focus:outline-none focus:ring-1"
            style={{ borderColor: '#E6E6E1' }}
          />
        )}
      </td>
      <td className="px-2 py-1.5 align-middle" style={{ width: 80 }}>
        {readOnly ? (
          <span className="text-xs block text-center" style={{ color: '#111827' }}>{ligne.taux_tva ?? 20} %</span>
        ) : tvaRates.length > 0 ? (
          <select value={ligne.taux_tva ?? 20}
            onChange={e => onChangeLigne(ligne._key, 'taux_tva', parseFloat(e.target.value))}
            className="text-xs px-1 py-0.5 border rounded w-full focus:outline-none focus:ring-1"
            style={{ borderColor: '#E6E6E1' }}>
            {tvaRates.map(r => <option key={r.taux} value={r.taux}>{r.taux} %</option>)}
          </select>
        ) : (
          <NumericInput
            value={ligne.taux_tva ?? 20}
            onChange={v => onChangeLigne(ligne._key, 'taux_tva', v)}
            min={0}
            className="text-xs px-1.5 py-0.5 border rounded w-full text-center focus:outline-none focus:ring-1"
            style={{ borderColor: '#E6E6E1' }}
          />
        )}
      </td>
      <td className="px-2 py-1.5 align-middle text-right" style={{ width: 100 }}>
        <span className="text-xs font-medium" style={{ color: '#111827' }}>
          {totalLigne.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
        </span>
      </td>
      {deleteBtn}
    </tr>
  );
}


// ─── FactureLignesTable + GhostRow ───────────────────────────────────────────

function FactureLignesTable({ lignes, setLignes, isDeviseMode = false, devise = 'EUR', tauxChange = null }) {
  const totalHT = lignes.reduce((s, lg) => {
    const pu_n = (lg.prix_unitaire_ht || 0) * (1 - (lg.remise || 0) / 100);
    return s + Math.round(pu_n * (lg.quantite || 1) * 100) / 100;
  }, 0);
  const onChangeLigne = (idx, field, value) => {
    setLignes(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const updated = { ...l, [field]: value };
      if (field === 'montant_devise' && tauxChange && tauxChange > 0) {
        updated.prix_unitaire_ht = Math.round((Number(value) || 0) / tauxChange * 10000) / 10000;
      }
      if (field === 'quantite' && isDeviseMode && tauxChange && tauxChange > 0 && l.montant_devise) {
        updated.prix_unitaire_ht = Math.round((Number(l.montant_devise) || 0) / tauxChange * 10000) / 10000;
      }
      return updated;
    }));
  };
  const colSpanFooter = isDeviseMode ? 6 : 5;

  if (lignes.length === 0 && !lignes._showTable) {
    return (
      <div className="mt-3 border rounded-xl overflow-visible" style={{ borderColor: 'var(--border-light)' }}>
        <table className="w-full text-xs"><tbody>
          <FactureGhostRow onAdd={lg => setLignes(prev => [...prev, { ...lg, sequence: 0 }])}
            isDeviseMode={isDeviseMode} devise={devise} tauxChange={tauxChange} />
        </tbody></table>
      </div>
    );
  }

  return (
    <div className="mt-3 border rounded-xl overflow-visible" style={{ borderColor: 'var(--border-light)' }}>
      <table className="w-full text-xs">
        <thead>
          <tr style={{ background: '#F8FAF8', borderBottom: '1px solid var(--border-light)', color: 'var(--text-muted)' }}>
            <th className="text-left px-3 py-2 font-semibold" style={{ width: 140 }}>Ref</th>
            <th className="text-left px-3 py-2 font-semibold">Designation</th>
            <th className="text-right px-3 py-2 font-semibold w-16">Qte</th>
            {isDeviseMode && (
              <th className="text-right px-3 py-2 font-semibold w-28" style={{ color: '#7C3AED' }}>Montant {devise}</th>
            )}
            <th className="text-right px-3 py-2 font-semibold w-24">PU HT EUR</th>
            <th className="text-right px-3 py-2 font-semibold w-12">TVA</th>
            <th className="text-right px-3 py-2 font-semibold w-24">Montant HT</th>
            <th className="text-left px-3 py-2 font-semibold" style={{ color: lignes.some(l => l.article_id && !l.compte_charge) ? '#DC2626' : 'var(--text-muted)' }}>Compte charge</th>
            <th className="w-6" />
          </tr>
        </thead>
        <tbody>
          {lignes.map((lg, idx) => {
            const pu_n = (lg.prix_unitaire_ht || 0) * (1 - (lg.remise || 0) / 100);
            const mht  = Math.round(pu_n * (lg.quantite || 1) * 100) / 100;
            return (
              <tr key={idx} style={{ borderTop: '1px solid var(--border-light)' }}>
                <td className="px-3 py-1.5" style={{ width: 140 }}>
                  {lg.article_id
                    ? <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: '#EEF2FF', color: '#4338CA', whiteSpace: 'nowrap' }}>
                        {lg.reference || '—'}
                      </span>
                    : <input value={lg.reference || ''} onChange={e => setLignes(p => p.map((l,i) => i===idx ? {...l, reference: e.target.value} : l))}
                        className="w-28 text-xs rounded border px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-300" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }} />
                  }
                </td>
                <td className="px-3 py-1.5">
                  <input value={lg.designation || ''} onChange={e => setLignes(p => p.map((l,i) => i===idx ? {...l, designation: e.target.value} : l))}
                    className="w-full text-xs rounded border px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-300" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
                </td>
                <td className="px-3 py-1.5">
                  <input type="number" value={lg.quantite ?? ''} onChange={e => onChangeLigne(idx, 'quantite', parseFloat(e.target.value)||1)}
                    className="w-16 text-xs text-right rounded border px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-300 tabular-nums" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
                </td>
                {isDeviseMode && (
                  <td className="px-2 py-1.5" style={{ width: 110 }}>
                    <input type="number" value={lg.montant_devise != null ? Math.round(lg.montant_devise * 100) / 100 : ''} step="0.01" onChange={e => {
                      if (e.target.value === '') return;
                      onChangeLigne(idx, 'montant_devise', parseFloat(e.target.value) || 0);
                    }}
                      className="text-xs px-1.5 py-0.5 border rounded w-full text-right focus:outline-none focus:ring-1 tabular-nums"
                      style={{ borderColor: '#C4B5FD', color: '#7C3AED', fontWeight: 500 }} />
                  </td>
                )}
                <td className="px-3 py-1.5" style={{ width: 100 }}>
                  {isDeviseMode ? (
                    <span className="text-xs block text-right px-1.5 py-0.5 rounded"
                      style={{ color: '#6B7280', background: '#F3F4F6', border: '1px solid #E5E7EB' }}>
                      {lg.prix_unitaire_ht != null && lg.prix_unitaire_ht !== 0
                        ? (Number(lg.prix_unitaire_ht)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '—'}
                    </span>
                  ) : (
                    <input type="number" value={lg.prix_unitaire_ht ?? ''} onChange={e => {
                      if (e.target.value === '') return; // état intermédiaire (ex: '.')
                      onChangeLigne(idx, 'prix_unitaire_ht', parseFloat(e.target.value) || 0);
                    }}
                      className="w-28 text-xs text-right rounded border px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-300 tabular-nums" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
                  )}
                </td>
                <td className="px-3 py-1.5 text-right">
                  <select value={lg.taux_tva ?? 0.20} onChange={e => setLignes(p => p.map((l,i) => i===idx ? {...l, taux_tva: parseFloat(e.target.value)} : l))}
                    className="text-xs border rounded px-1 py-1 focus:outline-none cursor-pointer"
                    style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
                    <option value={0}>0 %</option>
                    <option value={0.021}>2,1 %</option>
                    <option value={0.055}>5,5 %</option>
                    <option value={0.085}>8,5 %</option>
                    <option value={0.10}>10 %</option>
                    <option value={0.20}>20 %</option>
                  </select>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold" style={{ color: 'var(--text)' }}>
                  {mht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR
                </td>
                <td className="px-3 py-1.5">
                  <span className="text-xs px-1.5 py-0.5 rounded"
                    style={{ background: lg.compte_charge ? '#F0FDF4' : '#FEF2F2', color: lg.compte_charge ? '#15803D' : '#DC2626', fontWeight: lg.compte_charge ? 400 : 600 }}>
                    {lg.compte_charge || 'Manquant'}
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  <button onClick={() => setLignes(p => p.filter((_,i) => i!==idx))}
                    className="p-0.5 rounded hover:bg-red-50" style={{ color: '#DC2626' }}>
                    <X size={11} />
                  </button>
                </td>
              </tr>
            );
          })}
          <FactureGhostRow onAdd={lg => setLignes(prev => [...prev, { ...lg, sequence: prev.length }])}
            isDeviseMode={isDeviseMode} devise={devise} tauxChange={tauxChange} />
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid var(--border-light)', background: '#F8FAF8' }}>
            <td colSpan={colSpanFooter} className="px-3 py-2 text-xs font-semibold text-right" style={{ color: 'var(--text-muted)' }}>Total HT</td>
            <td className="px-3 py-2 text-right tabular-nums font-bold text-xs" style={{ color: 'var(--primary-solid)' }}>
              {totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR
            </td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function FactureGhostRow({ onAdd, isDeviseMode = false, devise = 'EUR', tauxChange = null }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos]         = useState(null);
  const [draft, setDraft]     = useState({ quantite: 1, prix_unitaire_ht: 0, taux_tva: 0.20 });
  const inputRef = useRef();
  const dropRef  = useRef();

  useEffect(() => {
    const h = e => {
      if (inputRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
      const r = await api.getArticles({ q: query || undefined, limit: 20, sort_by: 'reference', sort_dir: 1 });
        setResults(Array.isArray(r.data) ? r.data : (r.data?.items || []));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  const calcPos = () => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 480) });
  };

  const addFromArticle = (a) => {
    setOpen(false); setQuery(''); setResults([]);
    const puEur = a.prix_achat || 0;
    onAdd({
      article_id: a.article_id,
      designation: a.designation,
      reference: a.reference,
      quantite: draft.quantite || 1,
      prix_unitaire_ht: puEur,
      montant_devise: isDeviseMode && tauxChange && tauxChange > 0
        ? Math.round(puEur * tauxChange * 100) / 100
        : null,
      taux_tva: 0.20,
      remise: 0,
      compte_charge: a.compte_charge || null,
      famille_nom: a.famille_nom || null,
    });
    setDraft({ quantite: 1, prix_unitaire_ht: 0, taux_tva: 0.20 });
  };

  const addFreeText = () => {
    if (!query.trim()) return;
    onAdd({
      designation: query.trim(),
      quantite: draft.quantite || 1,
      prix_unitaire_ht: draft.prix_unitaire_ht || 0,
      taux_tva: draft.taux_tva || 0.20,
      remise: 0,
    });
    setQuery(''); setDraft({ quantite: 1, prix_unitaire_ht: 0, taux_tva: 0.20 });
  };

  const puLabel = isDeviseMode ? `PU ${devise}` : 'PU HT';

  return (
    <>
      <tr style={{ borderTop: '1px solid var(--border-light)' }}>
        {/* Désignation / article search */}
        <td className="px-3 py-1.5 relative" colSpan={2}>
          <div className="flex items-center gap-1.5 rounded border px-2 py-1 focus-within:ring-1 focus-within:ring-blue-300"
            style={{ borderColor: 'var(--border)', background: '#fff' }}>
            <Plus size={11} style={{ color: '#9CA3AF', flexShrink: 0 }} />
            <input ref={inputRef} value={query}
              onChange={e => { setQuery(e.target.value); calcPos(); setOpen(true); }}
              onFocus={() => { calcPos(); setOpen(true); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFreeText(); } }}
              placeholder="Ajouter une ligne — article ou texte libre…"
              className="flex-1 text-xs bg-transparent focus:outline-none"
              style={{ color: 'var(--text)' }} />
            {loading && <Loader2 size={11} className="animate-spin" style={{ color: '#9CA3AF' }} />}
          </div>
        </td>
        {/* Quantité */}
        <td className="px-3 py-1.5">
          <input type="number" value={draft.quantite || ''} onChange={e => setDraft(d => ({ ...d, quantite: parseFloat(e.target.value) || 1 }))}
            className="w-16 text-xs text-right border rounded px-1.5 py-1 focus:outline-none tabular-nums"
            style={{ borderColor: 'var(--border)' }} />
        </td>
        {/* PU — en devise si isDeviseMode */}
        <td className="px-3 py-1.5">
          <div className="flex items-center gap-1">
            <input type="number" value={draft.prix_unitaire_ht || ''} onChange={e => setDraft(d => ({ ...d, prix_unitaire_ht: parseFloat(e.target.value) || 0 }))}
              className="w-24 text-xs text-right border rounded px-1.5 py-1 focus:outline-none tabular-nums"
              style={{ borderColor: 'var(--border)' }} />
            {isDeviseMode && (
              <span className="text-xs font-semibold flex-shrink-0" style={{ color: '#2563EB' }}>{devise}</span>
            )}
          </div>
          {isDeviseMode && tauxChange && tauxChange > 0 && draft.prix_unitaire_ht > 0 && (
            <div className="text-xs tabular-nums" style={{ color: 'var(--text-muted)', marginTop: 2 }}>
              ≈ {(draft.prix_unitaire_ht / tauxChange).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
            </div>
          )}
        </td>
        {/* TVA */}
        <td className="px-3 py-1.5">
          <select value={draft.taux_tva ?? 0.20} onChange={e => setDraft(d => ({ ...d, taux_tva: parseFloat(e.target.value) }))}
            className="text-xs border rounded px-1 py-1 focus:outline-none" style={{ borderColor: 'var(--border)' }}>
            <option value={0}>0 %</option>
            <option value={0.021}>2,1 %</option>
            <option value={0.055}>5,5 %</option>
            <option value={0.085}>8,5 %</option>
            <option value={0.10}>10 %</option>
            <option value={0.20}>20 %</option>
          </select>
        </td>
        <td colSpan={3} className="px-3 py-1.5">
          {query.trim() && (
            <button onClick={addFreeText}
              className="text-xs px-2 py-1 rounded border font-medium"
              style={{ borderColor: 'var(--border)', color: 'var(--primary-solid)' }}>
              Ajouter texte libre
            </button>
          )}
        </td>
      </tr>

      {open && pos && results.length > 0 && (
        <tr style={{ display: 'contents' }}>
          <td>
            <div ref={dropRef}
              style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
                       background: 'white', border: '1px solid var(--border)',
                       borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                       maxHeight: 260, overflowY: 'auto', zIndex: 9999 }}>
              {results.map(a => (
                <button key={a.article_id} type="button"
                  onMouseDown={e => { e.preventDefault(); addFromArticle(a); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0 flex items-center gap-3 text-xs"
                  style={{ borderColor: 'var(--border-light)' }}>
                  <span className="font-semibold flex-shrink-0" style={{ color: 'var(--text)', minWidth: 80 }}>{a.reference || '—'}</span>
                  <span className="flex-1 truncate" style={{ color: 'var(--text-secondary)' }}>{a.designation}</span>
                  {a.compte_charge
                    ? <span className="text-xs px-1.5 rounded flex-shrink-0" style={{ background: '#F0FDF4', color: '#15803D' }}>{a.compte_charge}</span>
                    : <span className="text-xs px-1.5 rounded flex-shrink-0" style={{ background: '#FEF2F2', color: '#DC2626' }}>Sans compte</span>
                  }
                </button>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


// ─── GhostRowAchat ────────────────────────────────────────────────────────────
function GhostRowAchat({ onAdd }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const inputRef = useRef();
  const portalRef = useRef();

  const TYPE_OPTS = [
    { value: '',              label: 'Tous types' },
    { value: 'materiel',      label: 'Matériel' },
    { value: 'piece_detachee',label: 'Pièce détachée' },
    { value: 'prestation',    label: 'Prestation' },
    { value: 'main_oeuvre',   label: 'Main d\'œuvre' },
    { value: 'texte',         label: 'Commentaire' },
    { value: 'section',       label: 'Section' },
  ];

  const search = useCallback(async (q, type) => {
    setLoading(true);
    try {
      const params = { limit: 50, sort_by: 'reference', sort_dir: 1 };
      if (q && q.trim()) params.q = q.trim();
      if (type && type !== 'texte' && type !== 'section') params.type = type;
      else if (!type) params.exclude_type = 'location';
      const r = await api.getArticles(params);
      setResults((r.data.items || r.data || []).filter(a => a.type !== 'location'));
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query, typeFilter), 250);
    return () => clearTimeout(t);
  }, [query, typeFilter, search]);

  useEffect(() => {
    const h = (e) => {
      const inRow = inputRef.current?.closest('tr')?.contains(e.target);
      const inPortal = portalRef.current?.contains(e.target);
      if (!inRow && !inPortal) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const calcPos = () => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 440) });
  };

  const select = (art) => { onAdd(art); setQuery(''); setResults([]); setOpen(false); };
  const isSpecial = typeFilter === 'texte' || typeFilter === 'section';
  const placeholder = typeFilter === 'section' ? 'Titre de la section…' : typeFilter === 'texte' ? 'Saisissez votre texte…' : 'Rechercher un article…';

  const handleKeyDown = (e) => {
    if (isSpecial && e.key === 'Enter') {
      e.preventDefault();
      select({ type_ligne: typeFilter, designation: query });
      setQuery(''); setTypeFilter('');
    }
    if (!isSpecial && e.key === 'Enter' && results.length > 0) select(results[0]);
  };

  return (
    <>
      <tr style={{ borderTop: '1px solid #E6E6E1' }}>
        <td colSpan={8} className="px-3 py-2">
          <div className="flex items-center gap-2">
            <Plus size={13} style={{ color: '#9CA3AF', flexShrink: 0 }} />
            <TypeSelectDropdown value={typeFilter} opts={TYPE_OPTS} onChange={v => {
              setTypeFilter(v);
              setQuery('');
              setResults([]);
              if (v === 'texte' || v === 'section') {
                setOpen(false);
                setTimeout(() => inputRef.current?.focus(), 50);
              }
            }} />
            <input ref={inputRef} value={query}
              onChange={e => { setQuery(e.target.value); if (!isSpecial) { setOpen(true); calcPos(); } }}
              onFocus={() => { calcPos(); if (!isSpecial) { setOpen(true); if (results.length === 0) search(query, typeFilter); } }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              data-testid="ghost-row-achat-input"
              className="flex-1 text-xs border rounded px-2 focus:outline-none focus:ring-1"
              style={{
                borderColor: isSpecial ? (typeFilter === 'section' ? '#818CF8' : '#D97706') : '#E6E6E1',
                height: 28, color: '#1A1A1A',
                fontStyle: typeFilter === 'texte' ? 'italic' : 'normal',
                fontWeight: typeFilter === 'section' ? 600 : 400
              }} />
            {isSpecial && query.trim() && (
              <button type="button" onMouseDown={e => { e.preventDefault();
                select({ type_ligne: typeFilter, designation: query });
                setQuery(''); setTypeFilter(''); setOpen(false); }}
                className="text-xs px-2 py-0.5 rounded font-medium flex-shrink-0"
                style={{ background: typeFilter === 'section' ? '#EEF2FF' : '#FEF3C7',
                         color: typeFilter === 'section' ? '#4F46E5' : '#92400E', height: 28 }}>
                Ajouter
              </button>
            )}
          </div>
        </td>
      </tr>
      {typeof document !== 'undefined' && createPortal(
        <div ref={portalRef} style={{ pointerEvents: 'auto' }}>
          {!isSpecial && open && pos && (
            <div style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999, maxHeight: 320, overflowY: 'auto', background: 'white', borderRadius: 12, border: '1px solid #E6E6E1', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', pointerEvents: 'auto' }}>
              {loading && (
                <div className="px-4 py-3 text-xs" style={{ color: '#9CA3AF' }}>Recherche…</div>
              )}
              {!loading && results.length === 0 && (
                <div className="px-4 py-3 text-xs" style={{ color: '#9CA3AF' }}>Aucun article trouvé</div>
              )}
              {results.map(art => {
                const TYPE_COLOR = {
                  materiel:      { bg: '#EEF2FF', color: '#4F46E5' },
                  piece_detachee:{ bg: '#FFF7ED', color: '#C2410C' },
                  prestation:    { bg: '#F0FDF4', color: '#16A34A' },
                  main_oeuvre:   { bg: '#FDF4FF', color: '#9333EA' },
                };
                const tc = TYPE_COLOR[art.type] || { bg: '#F3F4F6', color: '#6B7280' };
                return (
                  <button key={art.article_id} type="button"
                    onMouseDown={() => select(art)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-blue-50 transition-colors text-xs"
                    style={{ borderBottom: '1px solid #F2F2EE' }}>
                    <span className="font-mono text-xs px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: '#EEF2FF', color: '#4338CA' }}>
                      {art.reference || '—'}
                    </span>
                    <span className="truncate font-medium" style={{ color: '#111827' }}>{art.designation}</span>
                    {art.type && (
                      <span className="ml-auto flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ background: tc.bg, color: tc.color }}>
                        {art.type === 'piece_detachee' ? 'Pièce' : art.type === 'main_oeuvre' ? 'M.O.' : art.type}
                      </span>
                    )}
                    {art.prix_achat != null && (
                      <span className="flex-shrink-0 text-xs" style={{ color: '#6B7280' }}>
                        {Number(art.prix_achat).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Zone combobox cherchable ─────────────────────────────────────────────────
function AchatForm({ editAchat, onClose, onSaved, fournisseurs, stocks, articles, familles, sousFamilles,
  onChangeStade, onDelete, isReadOnly, activeAgences, tvaRates, uniteDecimals = {}, achatProvider = 'odoo' }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');
  const { hasActivity } = useHasActivite('achat', editAchat?.achat_id);
  const [docCount, setDocCount] = useState(0);
  const [brs, setBRs] = useState([]);
  const [loadingBRs, setLoadingBRs] = useState(false);
  const [revisionsCount, setRevisionsCount] = useState(null);
  const [brsCount, setBrsCount] = useState(null);
  // ── Joindre facture — état local (modal dans le Sheet pour éviter le blocage Radix) ──
  const [localFichier, setLocalFichier] = useState(null);
  const [localSending, setLocalSending] = useState(false);
  const [showReceptionWarning, setShowReceptionWarning] = useState(false);

  const handleLocalSend = async () => {
    if (!editAchat || !localFichier) return;
    setLocalSending(true);
    try {
      const res = await api.envoyerFactureAchat(editAchat.achat_id, localFichier);
      onSaved(res.data);
      toast.success('Facture envoyée dans Odoo');
      setLocalFichier(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'envoi vers Odoo');
    } finally {
      setLocalSending(false);
    }
  };

  // ── Factures Achat ──────────────────────────────────────────────────────
  const [factures, setFactures] = useState([]);
  const [facturesCount, setFacturesCount] = useState(null);
  const [factureForm, setFactureForm] = useState(null);
  const [factureSaving, setFactureSaving] = useState(false);
  const [factureTauxLoading, setFactureTauxLoading] = useState(false);
  const [factureLignes, setFactureLignes] = useState([]); // lignes de la facture en cours d'édition
  const [factureLignesSaving, setFactureLignesSaving] = useState(false);
  const [pdfUploading, setPdfUploading] = useState({});
  const [odooSending,  setOdooSending]  = useState({});
  const [brCreating, setBrCreating] = useState({});
  const pdfInputRefs = useRef({});
  // Pour la facture d'acompte : HT calculé depuis TTC + taux TVA saisi
  const tvaAcompte = parseFloat(factureForm?.tva_acompte ?? 0);
  const htAcompte  = factureForm?.montant_ttc
    ? Math.round(parseFloat(factureForm.montant_ttc) / (1 + tvaAcompte / 100) * 100) / 100
    : 0;
  const acomptesDisponibles = factures.filter(f => f.type_facture === 'acompte');
  const totalAcomptesHT = acomptesDisponibles.reduce((s, f) => s + parseFloat(f.montant_ht || f.montant_ttc || 0), 0);
  const nbAcomptes = acomptesDisponibles.length;

  // Total HT des lignes en cours (pour affichage récapitulatif sur Solde)
  const totalLignesHT = factureLignes.reduce((s, l) => {
    const pu_net = (l.prix_unitaire_ht || 0) * (1 - (l.remise || 0) / 100);
    return s + pu_net * (l.quantite || 1);
  }, 0);

  const loadFactures = async (achat_id) => {
    if (!achat_id) return;
    try {
      const r = await api.getFacturesAchat(achat_id);
      const list = r.data || [];
      setFactures(list);
      setFacturesCount(list.length || null);
    } catch { /* silent */ }
  };

  const handleFactureFieldChange = async (key, val) => {
    setFactureForm(prev => ({ ...prev, [key]: val, montant_ht_eur: prev?.montant_ht_eur, montant_ttc_eur: prev?.montant_ttc_eur }));
    // Auto-fetch taux sur changement de date ou de devise
    const updated = { ...(factureForm || {}), [key]: val };
    const devise = updated.devise || editAchat?.devise_fournisseur || 'EUR';
    const dateF  = updated.date_facture;

    if (devise !== 'EUR' && dateF) {
      setFactureTauxLoading(true);
      try {
        const r = await api.getTauxPourDate(devise, dateF);
        const taux = r.data?.taux;

        if (taux && taux > 0 && factureLignes.length > 0) {
          // Recalculer prix_unitaire_ht (EUR) de chaque ligne depuis montant_devise + taux du jour
          const newLignes = factureLignes.map(l => {
            if (!l.montant_devise) return l;
            const puEur = Math.round((l.montant_devise || 0) / taux * 10000) / 10000;
            return { ...l, prix_unitaire_ht: puEur };
          });
          setFactureLignes(newLignes);
          // Totaux EUR depuis les lignes recalculées
          const htEur  = Math.round(newLignes.reduce((s, l) => {
            const pu_n = (l.prix_unitaire_ht || 0) * (1 - (l.remise || 0) / 100);
            return s + pu_n * (l.quantite || 1);
          }, 0) * 100) / 100;
          const ttcEur = Math.round(newLignes.reduce((s, l) => {
            const pu_n = (l.prix_unitaire_ht || 0) * (1 - (l.remise || 0) / 100);
            return s + pu_n * (l.quantite || 1) * (1 + (l.taux_tva || 0));
          }, 0) * 100) / 100;
          setFactureForm(prev => ({ ...prev, [key]: val, taux_change: taux, montant_ht_eur: htEur, montant_ttc_eur: ttcEur }));
        } else {
          // Pas de lignes : calcul depuis le montant global USD saisi
          const ht  = parseFloat(updated.montant_ht  || 0);
          const ttc = parseFloat(updated.montant_ttc || 0);
          setFactureForm(prev => ({
            ...prev, [key]: val, taux_change: taux,
            montant_ht_eur:  taux && ht  ? Math.round((ht  / taux) * 100) / 100 : prev?.montant_ht_eur,
            montant_ttc_eur: taux && ttc ? Math.round((ttc / taux) * 100) / 100 : prev?.montant_ttc_eur,
          }));
        }
      } catch { /* taux indisponible */ } finally { setFactureTauxLoading(false); }
    }
  };

  const handleSaveFacture = async () => {
    if (!factureForm) return;
    setFactureSaving(true);
    const payload = { ...factureForm };
    // En mode devise : écraser les montants avec les valeurs calculées depuis les lignes
    if (isDeviseMode && factureLignes.length > 0) {
      payload.montant_ht      = totalFactureDeviseHT;
      payload.montant_ttc     = totalFactureDeviseTTC;
      payload.montant_ht_eur  = totalFactureEurHT;
      payload.montant_ttc_eur = totalFactureEurTTC;
      if (factureTauxJour) payload.taux_change = factureTauxJour;
    }
    // Pour un acompte : calculer et injecter montant_ht + montant_tva depuis tva_acompte
    if (payload.type_facture === 'acompte' && payload.montant_ttc) {
      const tva  = parseFloat(payload.tva_acompte ?? 0);
      const ht   = Math.round(parseFloat(payload.montant_ttc) / (1 + tva / 100) * 100) / 100;
      const tvaM = Math.round((parseFloat(payload.montant_ttc) - ht) * 100) / 100;
      payload.montant_ht  = ht;
      payload.montant_tva = tvaM;
    }
    try {
      let facture_id;
      if (payload.facture_id) {
        const r = await api.updateFactureAchat(payload.facture_id, payload);
        setFactures(prev => prev.map(f => f.facture_id === payload.facture_id ? r.data : f));
        facture_id = payload.facture_id;
        toast.success('Facture mise à jour');
      } else {
        const r = await api.creerFactureAchat(editAchat.achat_id, payload);
        setFactures(prev => [...prev, r.data]);
        setFacturesCount(prev => (prev || 0) + 1);
        facture_id = r.data.facture_id;
        toast.success('Facture créée');
      }
      // Sauvegarder les lignes si présentes ET si ce n'est pas un acompte
      if (factureLignes.length > 0 && facture_id && factureForm.type_facture !== 'acompte') {
        setFactureLignesSaving(true);
        try {
          // prix_unitaire_ht est déjà en EUR dans factureLignes — sauvegarde directe sans conversion
          const lignesForDB = factureLignes.map(l => {
              const pu_n = (l.prix_unitaire_ht || 0) * (1 - (l.remise || 0) / 100);
              return {
                ...l,
                montant_ht:  Math.round(pu_n * (l.quantite || 1) * 100) / 100,
                montant_tva: Math.round(pu_n * (l.quantite || 1) * (l.taux_tva || 0) * 100) / 100,
                montant_ttc: Math.round(pu_n * (l.quantite || 1) * (1 + (l.taux_tva || 0)) * 100) / 100,
              };
            });
          const r2 = await api.saveFactureAchatLignes(facture_id, { lignes: lignesForDB });
          toast.success(`${r2.data.nb_lignes} ligne(s) sauvegardée(s) — total HT : ${r2.data.total_ht?.toLocaleString('fr-FR')} €`);
        } catch (e2) { toast.error(e2?.response?.data?.detail || 'Erreur lignes'); }
        finally { setFactureLignesSaving(false); }
      }
      setFactureForm(null);
      setFactureLignes([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setFactureSaving(false); }
  };

  const handleDeleteFacture = async (facture_id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await api.deleteFactureAchat(facture_id);
      setFactures(prev => prev.filter(f => f.facture_id !== facture_id));
      setFacturesCount(prev => Math.max(0, (prev || 1) - 1) || null);
      toast.success('Facture supprimée');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleUploadPdfFacture = async (facture_id, file) => {
    if (!file) return;
    setPdfUploading(p => ({ ...p, [facture_id]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.uploadPdfFactureAchat(facture_id, fd);
      setFactures(prev => prev.map(f => f.facture_id === facture_id ? { ...f, fichier_pdf_url: r.data.fichier_pdf_url, odoo_invoice_id: r.data.odoo_invoice_id } : f));
      if (r.data.odoo_warning) {
        toast.warning(r.data.odoo_warning);
      } else if (r.data.odoo_invoice_id) {
        toast.success('PDF enregistré et transmis à Odoo');
      } else {
        toast.success('PDF de la facture enregistré');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur upload PDF');
    } finally { setPdfUploading(p => ({ ...p, [facture_id]: false })); }
  };

  const handleSendToOdoo = async (facture_id) => {
    setOdooSending(p => ({ ...p, [facture_id]: true }));
    let pollingStarted = false;
    try {
      const r = await api.envoyerFacture(facture_id);
      const { job_id, provider } = r.data;

      // Pennylane : réponse immédiate avec job_id → polling
      if (job_id) {
        pollingStarted = true;
        const nomProv = provider === 'pennylane' ? 'Pennylane' : 'Comptabilité';
        let _envRetries = 0;
        const poll = async () => {
          try {
            const sr = await api.getEnvoyerFactureStatus(job_id);
            const st = sr.data;
            if (st.status === 'running') {
              setTimeout(poll, 2000);
            } else if (st.status === 'done') {
              const res = st.result || {};
              setFactures(prev => prev.map(f => f.facture_id === facture_id ? {
                ...f,
                odoo_invoice_id: res.odoo_invoice_id || f.odoo_invoice_id,
                pl_invoice_id: res.pl_invoice_id || f.pl_invoice_id,
                pl_invoice_number: res.pl_invoice_number || f.pl_invoice_number,
                pl_statut: res.pl_statut || f.pl_statut,
              } : f));
              setOdooSending(p => ({ ...p, [facture_id]: false }));
              toast.success(`Facture transmise à ${nomProv}`);
            } else {
              setOdooSending(p => ({ ...p, [facture_id]: false }));
              toast.error(st.message || `Erreur lors de l'envoi vers ${nomProv}`);
            }
          } catch (e) {
            const httpStatus = e.response?.status;
            const detail = e.response?.data?.detail;
            if (httpStatus === 404 && _envRetries < 3) {
              _envRetries++;
              setTimeout(poll, 3000);
              return;
            }
            setOdooSending(p => ({ ...p, [facture_id]: false }));
            toast.error(detail || e.message || 'Erreur lors du suivi de l\'envoi');
          }
        };
        setTimeout(poll, 2000);
        return;
      }

      // Odoo : réponse synchrone
      const nomProv = achatProvider === 'pennylane' ? 'Pennylane' : 'Odoo';
      setFactures(prev => prev.map(f => f.facture_id === facture_id ? {
        ...f,
        odoo_invoice_id: r.data.odoo_invoice_id || f.odoo_invoice_id,
        pl_invoice_id: r.data.pl_invoice_id || f.pl_invoice_id,
        pl_invoice_number: r.data.pl_invoice_number || f.pl_invoice_number,
      } : f));
      toast.success(`Facture transmise à ${nomProv}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      if (!pollingStarted) setOdooSending(p => ({ ...p, [facture_id]: false }));
    }
  };

  const handleCreerBrFromFacture = async (facture_id) => {
    setBrCreating(p => ({ ...p, [facture_id]: true }));
    try {
      const r = await api.createBrFromFacture(facture_id);
      toast.success('Bon de réception créé');
      navigate(`/bons-reception?br_id=${r.data.br_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création BR');
    } finally { setBrCreating(p => ({ ...p, [facture_id]: false })); }
  };

  // ── Révisions & Activité ──
  const [revisions, setRevisions] = useState([]);
  const [loadingTab, setLoadingTab] = useState(false);
  const [rappelConfirm, setRappelConfirm] = useState(null);
  const [rappelBanner, setRappelBanner] = useState(null);

  const loadTab = async (tab) => {
    if (!editAchat?.achat_id) return;
    setLoadingTab(true);
    try {
      if (tab === 'revisions') {
        const r = await api.getAchatRevisions(editAchat.achat_id);
        setRevisions(r.data || []);
        setRevisionsCount((r.data || []).length);
      }
      if (tab === 'factures') {
        await loadFactures(editAchat.achat_id);
      }
    } catch { /* silent */ }
    finally { setLoadingTab(false); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    loadTab(tab);
  };

  const handleRappeler = async (revId) => {
    const rev = revisions.find(r => r.revision_id === revId);
    if (!rev) return;
    setForm(f => ({ ...f, lignes: (rev.lignes || []).map((l, i) => ({ ...l, _key: Date.now() + i })), notes: rev.notes || '' }));
    setActiveTab('details');
    setRappelBanner({ numero_revision: rev.numero_revision });
    setRappelConfirm(null);
    toast.success(`Révision V${rev.numero_revision} chargée — enregistrez pour confirmer`);
    try { await api.rappelerRevisionAchat(editAchat.achat_id, revId); } catch { /* non bloquant */ }
  };

  // Dépôts filtrés par agence active + mémorisation du dernier choix
  const [form, setForm] = useState(() => {
    if (editAchat) {
      // Pré-calcul synchrone de la TVA dès l'initialisation (évite le flash 20%→0%)
      const preTaux = (ligne) => {
        // Respecter TOUJOURS la valeur explicitement sauvegardée
        if (ligne.taux_tva != null) return Number(ligne.taux_tva);
        // Pas de valeur sauvegardée → auto-résoudre depuis la famille de l'article
        if (ligne.type_ligne !== 'article' || !ligne.article_id) return 20;
        const art = articles?.find(a => a.article_id === ligne.article_id);
        const famille = familles?.find(f => f.famille_id === art?.famille_id);
        const tvaTypeId = famille?.tva_achat_type_id;
        if (!tvaTypeId || !tvaRates?.length) return 20;
        const match = tvaRates.find(r => r.tva_type_id === tvaTypeId);
        return match ? match.taux : 20;
      };
      return {
        fournisseur_id: editAchat.fournisseur_id || '',
        fournisseur_nom: editAchat.fournisseur_nom || '',
        contact_id: editAchat.contact_id || '',
        contact_nom: editAchat.contact_nom || '',
        titre: editAchat.titre || '',
        ref_fournisseur: editAchat.ref_fournisseur || '',
        conditions_paiement: editAchat.conditions_paiement || '',
        date_commande: editAchat.date_commande || new Date().toISOString().slice(0, 10),
        date_livraison_prevue: editAchat.date_livraison_prevue || '',
        notes: editAchat.notes || '',
        agence_id: editAchat.agence_id || null,
        lignes: (Array.isArray(editAchat.lignes) ? editAchat.lignes : (typeof editAchat.lignes === 'string' ? JSON.parse(editAchat.lignes || '[]') : [])).map(l => ({
          ...l,
          _key: l.ligne_id || Date.now() + Math.random(),
          article_ref: l.article_ref || l.reference || '',
          article_designation: l.article_designation || l.designation || '',
          taux_tva: preTaux(l),
        })),
      };
    }
    return { ...EMPTY_FORM };
  });
  const [saving, setSaving] = useState(false);
  const facturePanelRef = useRef(null);

  // ── Devise fournisseur & taux de change ──
  // selectedFournisseurDevise : capturée immédiatement lors de la sélection dans le dropdown
  // (fournisseurs du parent peut être vide si loadRefData n'a pas encore terminé)
  const [selectedFournisseurDevise, setSelectedFournisseurDevise] = useState(
    editAchat?.devise_fournisseur || 'EUR'
  );
  const fournisseurDevise = useMemo(() => {
    // Priorité 1 : liste complète chargée (post-loadRefData) → source la plus fiable
    const fromList = fournisseurs.find(f => f.entreprise_id === form.fournisseur_id)?.devise;
    if (fromList) return fromList;
    // Priorité 2 : devise capturée lors de la sélection (disponible immédiatement)
    if (selectedFournisseurDevise && form.fournisseur_id) return selectedFournisseurDevise;
    return 'EUR';
  }, [fournisseurs, form.fournisseur_id, selectedFournisseurDevise]);
  const isDeviseMode = fournisseurDevise !== 'EUR';

  // Sync selectedFournisseurDevise quand la liste fournisseurs se charge (achats existants)
  useEffect(() => {
    if (!form.fournisseur_id || !fournisseurs.length) return;
    const fourn = fournisseurs.find(f => f.entreprise_id === form.fournisseur_id);
    if (fourn?.devise) setSelectedFournisseurDevise(fourn.devise);
  }, [fournisseurs, form.fournisseur_id]);

  // ── TVA Achat — résolution depuis le CP de l'agence émettrice (identique aux ventes) ──
  // Priorité 1 : agence de la commande (form.agence_id trouvé dans activeAgences)
  // Priorité 2 : première agence active du contexte
  const [resolvedTvaRates, setResolvedTvaRates] = useState(tvaRates || []);
  useEffect(() => {
    let cp = '';
    if (form.agence_id && activeAgences?.length) {
      const ag = activeAgences.find(a => a.agence_id === form.agence_id);
      if (ag) cp = ag.code_postal || '';
    }
    if (!cp && activeAgences?.length) cp = activeAgences[0]?.code_postal || '';
    api.resolveTva('achat', cp)
      .then(r => {
        const rates = (r.data?.taux_disponibles || []).map(t => ({
          taux: t.taux,
          designation: `${t.taux}%`,
          tva_type_id: t.tva_type_id,
        }));
        if (rates.length > 0) setResolvedTvaRates(rates);
      })
      .catch(() => {});
  }, [form.agence_id, activeAgences]);

  // Quand les taux résolus (territoire précis) arrivent, raffiner la TVA des lignes existantes.
  // Le pré-calcul synchrone (cf. useState ci-dessus) utilise les taux globaux — ce useEffect
  // affine avec les taux exacts du territoire de l'agence (sans flash si le résultat est identique).
  // RÈGLE : ne jamais écraser une valeur TVA explicitement sauvegardée (taux_tva != null).
  useEffect(() => {
    if (resolvedTvaRates.length === 0 || articles.length === 0 || familles.length === 0) return;
    setForm(prev => {
      const lignes = prev.lignes.map(ligne => {
        if (ligne.type_ligne !== 'article' || !ligne.article_id) return ligne;
        // Respecter les valeurs explicitement sauvegardées
        if (ligne.taux_tva != null) return ligne;
        const art = articles.find(a => a.article_id === ligne.article_id);
        const famille = familles.find(f => f.famille_id === art?.famille_id);
        const tvaTypeId = famille?.tva_achat_type_id;
        if (tvaTypeId == null) return ligne;
        const newTaux = _resolveTauxTva(tvaTypeId, resolvedTvaRates);
        if (newTaux === Number(ligne.taux_tva)) return ligne;
        return { ...ligne, taux_tva: newTaux };
      });
      const changed = lignes.some((l, i) => l !== prev.lignes[i]);
      return changed ? { ...prev, lignes } : prev;
    });
  }, [resolvedTvaRates, articles, familles]);

  const [tauxChange, setTauxChange] = useState(null);
  const [tauxLoading, setTauxLoading] = useState(false);

  useEffect(() => {
    if (!isDeviseMode) { setTauxChange(null); return; }
    setTauxLoading(true);
    api.getTauxChange(fournisseurDevise, form.date_commande || undefined)
      .then(r => setTauxChange(r.data?.taux ?? null))
      .catch(() => setTauxChange(null))
      .finally(() => setTauxLoading(false));
  }, [isDeviseMode, fournisseurDevise, form.date_commande]);

  // Totaux facture calculés depuis les lignes (doivent être après tauxChange)
  const factureTauxJour = factureForm?.taux_change || tauxChange;
  const totalFactureDeviseHT = isDeviseMode
    ? Math.round(factureLignes.reduce((s,l) => s + (l.montant_devise||0)*(l.quantite||1)*(1-(l.remise||0)/100), 0)*100)/100
    : 0;
  const totalFactureDeviseTTC = isDeviseMode
    ? Math.round(factureLignes.reduce((s,l) => { const m=(l.montant_devise||0)*(l.quantite||1)*(1-(l.remise||0)/100); return s+m*(1+(l.taux_tva||0)); },0)*100)/100
    : 0;
  const totalFactureEurHT  = Math.round(factureLignes.reduce((s,l) => { const pu=(l.prix_unitaire_ht||0)*(1-(l.remise||0)/100); return s+pu*(l.quantite||1); },0)*100)/100;
  const totalFactureEurTTC = Math.round(factureLignes.reduce((s,l) => { const pu=(l.prix_unitaire_ht||0)*(1-(l.remise||0)/100); return s+pu*(l.quantite||1)*(1+(l.taux_tva||0)); },0)*100)/100;

  // ── Email modal ──
  const [showMailModal, setShowMailModal] = useState(false);
  const [mailChips, setMailChips]         = useState([]);
  const [mailManualInput, setMailManualInput] = useState('');
  const [mailSujet, setMailSujet]         = useState('');
  const [mailCorps, setMailCorps]         = useState('');
  const [sendingMail, setSendingMail]     = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [envoyerMenuOpen, setEnvoyerMenuOpen] = useState(false);
  const envoyerMenuRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (envoyerMenuRef.current && !envoyerMenuRef.current.contains(e.target)) setEnvoyerMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openMailModal = () => {
    if (!editAchat) return;
    const numero      = editAchat.numero || '';
    const fournisseur = form.fournisseur_nom || '';
    const isCommande  = !['devis', 'brouillon', 'devis_envoye'].includes(editAchat.stade);
    const titre       = isCommande ? 'Bon de commande' : 'Demande de prix';
    // Pré-remplir le contact sélectionné (avec fallback si contacts pas encore chargés)
    const mainContact = contacts.find(c => (c.personne_id || c.contact_id) === form.contact_id);
    const contactEmail = mainContact?.email || editAchat?.contact_email || '';
    const chips = contactEmail
      ? [{ email: contactEmail, label: form.contact_nom || contactEmail }]
      : [];
    setMailChips(chips);
    setMailManualInput('');
    setMailSujet(`${titre} ${numero} — ${fournisseur}`.trim());
    setMailCorps(`Bonjour,\n\nVeuillez trouver ci-joint notre ${titre.toLowerCase()} n°\u00a0${numero}.\n\nCordialement,`);
    setShowMailModal(true);
  };

  const handleSendMail = async () => {
    const manualEmails = mailManualInput.split(',').map(e => e.trim()).filter(e => e.includes('@'));
    const all = [...new Set([...mailChips.map(c => c.email), ...manualEmails])];
    if (!all.length) { toast.error('Au moins un destinataire requis'); return; }
    setSendingMail(true);
    try {
      await api.envoyerEmailAchat(editAchat.achat_id, { destinataires: all, sujet: mailSujet, corps: mailCorps });
      toast.success(`Email envoyé à ${all.join(', ')}`);
      setShowMailModal(false);
      // Transition automatique vers "Demande envoyée" après envoi email
      if (['devis', 'brouillon'].includes(editAchat.stade)) {
        onChangeStade?.('devis_envoye');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi email');
    } finally { setSendingMail(false); }
  };

  const handleTelechargerPdf = async () => {
    if (!editAchat) return;
    setDownloadingPdf(true);
    try {
      const resp = await api.getAchatPdf(editAchat.achat_id);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${editAchat.numero || editAchat.achat_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    } finally { setDownloadingPdf(false); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      setForm(f => {
        const ids = f.lignes.map(l => l._key);
        return { ...f, lignes: arrayMove(f.lignes, ids.indexOf(active.id), ids.indexOf(over.id)) };
      });
    }
  };

  const addLigne = () => {
    setForm(f => ({
      ...f,
      lignes: [...f.lignes, { ...EMPTY_LIGNE, _key: Date.now() }],
    }));
  };

  const removeLigne = (key) => setForm(f => ({ ...f, lignes: f.lignes.filter(l => l._key !== key) }));

  const updateLigne = (key, field, value) => {
    setForm(f => ({
      ...f,
      lignes: f.lignes.map(l => {
        if (l._key !== key) return l;
        const updated = { ...l, [field]: value };
        if (field === 'article_id') {
          const art = articles.find(a => a.article_id === value);
          if (art) {
            updated.article_ref = art.reference || '';
            updated.article_designation = art.designation || '';
            updated.prix_unitaire_ht = art.prix_achat || 0;
            updated.unite_symbole = art.unite_symbole || null;
            // Résolution TVA depuis la matrice (CP fournisseur)
            updated.taux_tva = _resolveTauxTva(
              familles.find(f => f.famille_id === art.famille_id)?.tva_achat_type_id,
              resolvedTvaRates
            );
            // Auto-fill compte_charge from sous-famille or famille
            if (art.sous_famille_id) {
              const sf = sousFamilles.find(s => s.sous_famille_id === art.sous_famille_id);
              if (sf?.compte_charge) { updated.compte_charge = sf.compte_charge; }
              else if (sf?.famille_id) {
                const fam = familles.find(f2 => f2.famille_id === sf.famille_id);
                if (fam?.compte_charge) updated.compte_charge = fam.compte_charge;
              }
            } else if (art.famille_id) {
              const fam = familles.find(f2 => f2.famille_id === art.famille_id);
              if (fam?.compte_charge) updated.compte_charge = fam.compte_charge;
            }
          }
        }
        // Auto-calcul P.U. HT depuis montant en devise étrangère
        if (field === 'montant_devise' && tauxChange && tauxChange > 0) {
          updated.prix_unitaire_ht = value > 0
            ? parseFloat((value / tauxChange).toFixed(4))
            : 0;
        }
        return updated;
      }),
    }));
  };

  const totals = useMemo(() => {
    const artLines = form.lignes.filter(l => !l.type_ligne || l.type_ligne === 'article');
    const ht = artLines.reduce((s, l) => s + (Number(l.quantite_commandee) || 0) * (Number(l.prix_unitaire_ht) || 0), 0);
    const tva = artLines.reduce((s, l) => s + (Number(l.quantite_commandee) || 0) * (Number(l.prix_unitaire_ht) || 0) * (Number(l.taux_tva) || 0) / 100, 0);
    return { ht: ht.toFixed(2), tva: tva.toFixed(2), ttc: (ht + tva).toFixed(2) };
  }, [form.lignes]);

  const handleSave = async () => {
    if (!form.fournisseur_id) { toast.error('Sélectionnez un fournisseur'); return; }
    if (form.lignes.length === 0) { toast.error('Ajoutez au moins une ligne'); return; }
    for (const l of form.lignes) {
      if (l.type_ligne === 'section' || l.type_ligne === 'texte') continue;
      if (!l.article_id) { toast.error('Sélectionnez un article pour chaque ligne'); return; }
      if (!l.quantite_commandee || l.quantite_commandee <= 0) { toast.error('La quantité doit être > 0'); return; }
    }
    setSaving(true);
    const payload = {
      // En modification : ne jamais écraser l'agence d'origine avec le contexte UI actif.
      // En création : affecter l'agence du contexte (si une seule agence active).
      agence_id: editAchat
        ? null  // null → le backend skip ce champ (if val is not None guard)
        : (activeAgences?.length === 1 ? activeAgences[0].agence_id : null),
      fournisseur_id: form.fournisseur_id,
      fournisseur_nom: form.fournisseur_nom,
      contact_id: form.contact_id || null,
      contact_nom: form.contact_nom || null,
      titre: form.titre || null,
      ref_fournisseur: form.ref_fournisseur || null,
      conditions_paiement: form.conditions_paiement || null,
      date_commande: form.date_commande,
      date_livraison_prevue: form.date_livraison_prevue || null,
      notes: form.notes || null,
      lignes: form.lignes.map(({ _key, article_ref, ...rest }) => rest),
      devise_fournisseur: fournisseurDevise,
      taux_change_applique: isDeviseMode ? (tauxChange ?? null) : null,
    };
    try {
      let res;
      if (editAchat) {
        res = await api.updateAchat(editAchat.achat_id, payload);
        toast.success('Bon d\'achat modifié');
        // Créer une révision après chaque modification
        api.createAchatRevision(editAchat.achat_id).catch(() => {});
      } else {
        res = await api.createAchat(payload);
        toast.success('Bon d\'achat créé');
      }
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally { setSaving(false); }
  };

  const inp = "w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2";
  const inpSt = { borderColor: '#E6E6E1', background: '#FFFFFF', color: '#1A1A1A' };
  const inpRow = { borderColor: '#E6E6E1', background: '#FFFFFF', color: '#1A1A1A' };

  // ── Recherche async fournisseur (comme Ventes) ──
  const [fornQuery, setFornQuery] = useState('');
  const [fornOpen, setFornOpen] = useState(false);
  const [fornResults, setFornResults] = useState([]);
  const fornRef = useRef(null);

  const searchFournisseurs = useCallback(async (q) => {
    if (!q || q.length < 2) { setFornResults([]); return; }
    try {
      const r = await api.getEntreprises({ search: q, per_page: 15, exclude_gele: true });
      setFornResults(r.data.data || r.data || []);
    } catch { setFornResults([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchFournisseurs(fornQuery), 250);
    return () => clearTimeout(t);
  }, [fornQuery, searchFournisseurs]);

  useEffect(() => {
    const h = (e) => { if (fornRef.current && !fornRef.current.contains(e.target)) setFornOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Contacts (interlocuteur) ──
  const [contacts, setContacts] = useState([]);
  const [contactDropOpen, setContactDropOpen] = useState(false);
  const contactRef = useRef(null);

  const loadContacts = useCallback((entreprise_id) => {
    if (!entreprise_id) { setContacts([]); return; }
    api.getPersonnes({ entreprise_id, per_page: 100 })
      .then(r => setContacts(r.data.data || r.data || []))
      .catch(() => setContacts([]));
  }, []);

  useEffect(() => { loadContacts(form.fournisseur_id); }, [form.fournisseur_id, loadContacts]);

  useEffect(() => {
    const h = (e) => { if (contactRef.current && !contactRef.current.contains(e.target)) setContactDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Charger les sources liées (smart buttons) ──
  const [linkedSources, setLinkedSources] = useState({ ors: [], ventes: [] });
  const [linkedBRs, setLinkedBRs] = useState([]);
  useEffect(() => {
    if (!editAchat?.achat_id) { setLinkedSources({ ors: [], ventes: [] }); setLinkedBRs([]); return; }
    api.getAchatLinkedSources(editAchat.achat_id)
      .then(r => setLinkedSources(r.data || { ors: [], ventes: [] }))
      .catch(() => setLinkedSources({ ors: [], ventes: [] }));
    api.getAchatBonsReception(editAchat.achat_id)
      .then(r => setLinkedBRs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLinkedBRs([]));
  }, [editAchat?.achat_id]);

  // ── Charger les compteurs de tous les onglets dès l'ouverture ──
  useEffect(() => {
    if (!editAchat?.achat_id) return;
    Promise.all([
      api.getAchatRevisions(editAchat.achat_id),
      api.getAchatBonsReception(editAchat.achat_id),
      api.getAchatDocuments(editAchat.achat_id),
    ]).then(([revRes, brRes, docRes]) => {
      setRevisionsCount((revRes.data || []).length);
      setBrsCount((brRes.data || []).length);
      setDocCount((docRes.data || []).length);
    }).catch(() => {});
  }, [editAchat?.achat_id]);

  // ── Charger les bons de réception liés ──
  useEffect(() => {
    if (activeTab === 'receptions' && editAchat?.achat_id) {
      setLoadingBRs(true);
      api.getAchatBonsReception(editAchat.achat_id)
        .then(r => { setBRs(r.data || []); setBrsCount((r.data || []).length); })
        .catch(() => {})
        .finally(() => setLoadingBRs(false));
    }
  }, [activeTab, editAchat?.achat_id]);

  // ── Ajouter depuis GhostRow (article, section ou texte) ──
  const addLigneFromArticle = (art) => {
    const typeLigne = art.type_ligne || 'article';
    if (typeLigne === 'section' || typeLigne === 'texte') {
      setForm(f => ({
        ...f,
        lignes: [...f.lignes, {
          _key: Date.now(),
          type_ligne: typeLigne,
          designation: art.designation || '',
        }],
      }));
      return;
    }
    const sf = sousFamilles.find(s => s.sous_famille_id === art.sous_famille_id);
    const fam = familles.find(f => f.famille_id === (sf?.famille_id || art.famille_id));
    const compte_charge = sf?.compte_charge || fam?.compte_charge || '';
    setForm(f => ({
      ...f,
      lignes: [...f.lignes, {
        _key: Date.now(),
        type_ligne: 'article',
        type_article: art.type || '',
        article_id: art.article_id,
        article_ref: art.reference || '',
        article_designation: art.designation || '',
        quantite_commandee: 1,
        prix_unitaire_ht: art.prix_achat || 0,
        taux_tva: _resolveTauxTva(
          familles.find(f => f.famille_id === art.famille_id)?.tva_achat_type_id,
          resolvedTvaRates
        ),
        compte_charge,
        unite_symbole: art.unite_symbole || null,
      }],
    }));
  };

  // ── Footer ──
  const footer = (
    <div className="flex items-end justify-between gap-6">
      <div className="flex gap-6 text-sm">
        <div>
          <div className="text-xs mb-0.5" style={{ color: '#6B7280' }}>Total HT</div>
          <div className="font-semibold" style={{ color: '#1A1A1A' }}>{Number(totals.ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
        </div>
        <div>
          <div className="text-xs mb-0.5" style={{ color: '#6B7280' }}>TVA</div>
          <div className="font-semibold" style={{ color: '#1A1A1A' }}>{Number(totals.tva).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
        </div>
        <div>
          <div className="text-xs mb-0.5 font-bold" style={{ color: '#1A1A1A' }}>Total TTC</div>
          <div className="text-lg font-bold" style={{ color: 'var(--primary-solid)' }}>{Number(totals.ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
        </div>
      </div>
      <div className="flex gap-2">
        {/* Supprimer — brouillon uniquement */}
        {['devis', 'brouillon', 'devis_envoye'].includes(editAchat?.stade) && !isReadOnly && (
          <button onClick={() => onDelete?.()} data-testid="btn-delete-achat"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all hover:opacity-90"
            style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
            <Trash2 size={14} /> Supprimer
          </button>
        )}
        {/* Enregistrer */}
        <button onClick={handleSave} disabled={saving} data-testid="save-achat-btn"
          className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
          style={{ background: 'var(--primary-gradient)' }}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {editAchat ? 'Enregistrer' : 'Créer la demande de prix'}
        </button>
      </div>
    </div>
  );

  const headerSub = null; // unused

  return (
    <>
    <SidePanel
      open={true}
      onOpenChange={(v) => { if (!v) onClose(); }}
      title={editAchat ? editAchat.numero : 'Nouvelle demande de prix'}
      copyValue={editAchat?.numero}
      subtitle={editAchat?.stade === 'devis' ? 'Demande de prix' : 'Commande fournisseur'}
      size="xl"
      footer={footer}
      headerCenter={editAchat && (linkedSources.ors.length > 0 || linkedSources.ventes.length > 0 || linkedBRs.length > 0) ? (
        <div style={{ display: 'flex', gap: 8 }}>
          {linkedBRs.length > 0 && (
            <SmartButton
              icon={<PackageCheck size={13} />}
              label="Bons de réception"
              count={linkedBRs.length}
              color="blue"
              onClick={() => {
                if (linkedBRs.length === 1) {
                  navigate(`/bons-reception?open_br=${linkedBRs[0].br_id}`);
                } else {
                  const ids = linkedBRs.map(b => b.br_id).join(',');
                  navigate(`/bons-reception?filter_ids=${ids}&source_label=${encodeURIComponent(editAchat?.numero || '')}`);
                }
                onClose();
              }}
            />
          )}
          {linkedSources.ors.length > 0 && (
            <SmartButton
              icon={<Wrench size={13} />}
              label="Ordres de réparation"
              count={linkedSources.ors.length}
              color="orange"
              onClick={() => {
                if (linkedSources.ors.length === 1) {
                  navigate(`/atelier?open_or=${linkedSources.ors[0].id}`);
                } else {
                  const ids = linkedSources.ors.map(o => o.id).join(',');
                  navigate(`/atelier?filter_ids=${ids}&source_label=${encodeURIComponent(editAchat?.numero || '')}`);
                }
                onClose();
              }}
            />
          )}
          {linkedSources.ventes.length > 0 && (
            <SmartButton
              icon={<FileText size={13} />}
              label="Ventes"
              count={linkedSources.ventes.length}
              color="green"
              onClick={() => {
                if (linkedSources.ventes.length === 1) {
                  navigate(`/ventes?open_devis=${linkedSources.ventes[0].id}`);
                } else {
                  const ids = linkedSources.ventes.map(v => v.id).join(',');
                  navigate(`/ventes?filter_ids=${ids}&source_label=${encodeURIComponent(editAchat?.numero || '')}`);
                }
                onClose();
              }}
            />
          )}
        </div>
      ) : null}
      headerExtra={editAchat ? (
        <div className="flex items-center gap-2">
        </div>
      ) : null}
    >
      {/* ── Workflow buttons (comme /ventes) — avant la barre d'onglets ── */}
      {editAchat && (
        <div className="flex gap-2 flex-wrap items-center mb-2">
          {/* Dropdown "Envoyer" — PDF + Email */}
          <div className="relative" ref={envoyerMenuRef}>
            <button
              data-testid="btn-envoyer-achat"
              onClick={() => setEnvoyerMenuOpen(o => !o)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: '#1D4ED8' }}>
              <Send size={13} />
              {['devis', 'brouillon', 'devis_envoye'].includes(editAchat.stade) ? 'Envoyer la demande' : 'Envoyer le BC'}
              <ChevronDown size={13} style={{ marginLeft: 2 }} />
            </button>
            {envoyerMenuOpen && (
              <div className="absolute left-0 top-full mt-1.5 rounded-xl border bg-white shadow-xl z-50 overflow-hidden"
                style={{ borderColor: '#E6E6E1', minWidth: 210 }}>
                <button
                  data-testid="btn-envoyer-email-achat"
                  onClick={() => { setEnvoyerMenuOpen(false); openMailModal(); }}
                  className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors border-b"
                  style={{ borderColor: '#F3F4F6' }}>
                  <Mail size={14} style={{ color: '#1D4ED8' }} />
                  <div>
                    <div className="text-sm font-medium" style={{ color: '#111827' }}>Envoyer par email</div>
                    <div className="text-xs" style={{ color: '#6B7280' }}>PDF en pièce jointe</div>
                  </div>
                </button>
                <button
                  data-testid="btn-download-pdf-achat"
                  onClick={() => { setEnvoyerMenuOpen(false); handleTelechargerPdf(); }}
                  disabled={downloadingPdf}
                  className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors disabled:opacity-50">
                  {downloadingPdf ? <Loader2 size={14} className="animate-spin" style={{ color: '#6B7280' }} /> : <FileText size={14} style={{ color: '#6B7280' }} />}
                  <div>
                    <div className="text-sm font-medium" style={{ color: '#111827' }}>Imprimer / PDF</div>
                    <div className="text-xs" style={{ color: '#6B7280' }}>Ouvre le PDF dans un nouvel onglet</div>
                  </div>
                </button>
              </div>
            )}
          </div>
          {/* Confirmer la commande */}
          {['devis', 'brouillon', 'devis_envoye'].includes(editAchat.stade) && !isReadOnly && (
            <button onClick={() => onChangeStade?.('commande')} data-testid={`btn-commande-${editAchat.achat_id}`}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: 'var(--primary-gradient)' }}>
              <PackageCheck size={13} /> Confirmer la commande
            </button>
          )}
          {/* Annuler */}
          {['devis', 'brouillon', 'devis_envoye', 'commande'].includes(editAchat.stade) && !isReadOnly && (
            <button
              data-testid={`btn-annuler-commande-${editAchat.achat_id}`}
              onClick={() => {
                if (!window.confirm(`Annuler le bon "${editAchat.numero}" ?\n\nCette action est irréversible.`)) return;
                onChangeStade?.('annule');
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
              <X size={13} /> Annuler
            </button>
          )}
          {/* ── Boutons de transition ── */}
          {['commande', 'en_cours_reception'].includes(editAchat.stade) && !isReadOnly && !editAchat.tag_receptionne && (
            <>
              {/* Joindre facture → onglet Factures (style violet) */}
              <button
                onClick={() => { setActiveTab('factures'); loadFactures(editAchat.achat_id); }}
                data-testid="btn-goto-factures"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: '#7C3AED' }}>
                <Receipt size={13} /> Joindre facture
                {facturesCount > 0 && (
                  <span className="ml-1 bg-white text-purple-700 text-xs rounded-full px-1.5 py-0.5 leading-none font-bold">{facturesCount}</span>
                )}
              </button>

              {/* Réceptionner — popup d'avertissement */}
              <button
                onClick={() => setShowReceptionWarning(true)}
                data-testid="btn-receptionner-warning"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: '#16A34A' }}>
                <PackageCheck size={13} /> Réceptionner
              </button>
            </>
          )}
        </div>
      )}
      {/* ── Barre d'onglets (dans le body, comme /ventes) ── */}
      {editAchat && (
        <div className="flex border-b -mx-6 px-6 mb-5" style={{ borderColor: '#E6E6E1' }}>
          {[
            { key: 'details',      label: 'Détails',     icon: <FileText size={13} />,       count: null },
            { key: 'factures',     label: 'Factures',    icon: <Receipt size={13} />,         count: facturesCount },
            { key: 'echeances',    label: 'Échéances',   icon: <Clock size={13} />,            count: null },
            { key: 'revisions',    label: 'Révisions',   icon: <RotateCcw size={13} />,      count: revisionsCount },
            { key: 'receptions',   label: 'Réceptions',  icon: <PackageCheck size={13} />,    count: brsCount       },
            { key: 'documents',    label: 'Documents',   icon: <FolderOpen size={13} />,      count: docCount || null },
            { key: 'activites',    label: 'Activités',   icon: hasActivity ? <ActivityPulse /> : <Clock size={13} />,           count: null },
          ].map(tab => (
            <button key={tab.key} onClick={() => handleTabChange(tab.key)}
              data-testid={`achat-tab-${tab.key}`}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px"
              style={{ borderColor: activeTab === tab.key ? 'var(--primary-solid)' : 'transparent',
                       color: activeTab === tab.key ? 'var(--primary-solid)' : '#6B7280', background: 'transparent' }}>
              {tab.icon}{tab.label}
              <TabBadge n={tab.count} />
            </button>
          ))}
        </div>
      )}
      {/* ── Onglet Factures ── */}
      {activeTab === 'factures' && (
        <div className="space-y-4" data-testid="achat-tab-factures-content">
          {/* En-tête */}
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#6B7280' }}>
              Factures fournisseur
            </h3>
            <button onClick={() => {
                const lignesBrutes = (editAchat?.lignes || [])
                  .filter(l => !['section','texte'].includes(l.type_ligne));
                const lignesPreremplies = lignesBrutes.map((l, i) => {
                  // Normalisation taux_tva : DB stocke en % (20.0), FactureLignesTable attend décimal (0.20)
                  const tvaBrut = l.taux_tva != null ? parseFloat(l.taux_tva) : 0;
                  const tvaDecimal = tvaBrut > 1 ? tvaBrut / 100 : tvaBrut;
                  return {
                    achat_ligne_id: l.ligne_id,
                    article_id:     l.article_id,
                    materiel_id:    l.materiel_id || null,
                    designation:    l.designation || l.article_designation || '',
                    reference:      l.article_ref || '',
                    quantite:       l.quantite_commandee || 1,
                    prix_unitaire_ht: l.prix_unitaire_ht || 0,
                    montant_devise:   isDeviseMode ? (l.montant_devise || null) : null,
                    taux_tva:       tvaDecimal,
                    remise:         l.remise || 0,
                    compte_charge:  l.compte_charge || null,
                    famille_nom:    l.famille_nom || null,
                    sequence: i,
                  };
                });
                // Calcul totaux selon la devise
                let totalHT, totalTTC, totalHT_eur, totalTTC_eur;
                if (isDeviseMode) {
                  // Totaux en devise (USD) depuis montant_devise
                  totalHT = Math.round(lignesPreremplies.reduce((s, l) => {
                    const mt = (l.montant_devise || 0) * (1 - (l.remise || 0) / 100);
                    return s + mt;
                  }, 0) * 100) / 100;
                  totalTTC = Math.round(lignesPreremplies.reduce((s, l) => {
                    const mt = (l.montant_devise || 0) * (1 - (l.remise || 0) / 100);
                    return s + mt * (1 + (l.taux_tva || 0));
                  }, 0) * 100) / 100;
                  // Équivalents EUR depuis prix_unitaire_ht
                  totalHT_eur = Math.round(lignesPreremplies.reduce((s, l) => {
                    const pu_n = (l.prix_unitaire_ht || 0) * (1 - (l.remise || 0) / 100);
                    return s + pu_n * (l.quantite || 1);
                  }, 0) * 100) / 100;
                  totalTTC_eur = Math.round(lignesPreremplies.reduce((s, l) => {
                    const pu_n = (l.prix_unitaire_ht || 0) * (1 - (l.remise || 0) / 100);
                    return s + pu_n * (l.quantite || 1) * (1 + (l.taux_tva || 0));
                  }, 0) * 100) / 100;
                } else {
                  totalHT = Math.round(lignesPreremplies.reduce((s, l) => {
                    const pu_n = (l.prix_unitaire_ht || 0) * (1 - (l.remise || 0) / 100);
                    return s + pu_n * (l.quantite || 1);
                  }, 0) * 100) / 100;
                  totalTTC = Math.round(lignesPreremplies.reduce((s, l) => {
                    const pu_n = (l.prix_unitaire_ht || 0) * (1 - (l.remise || 0) / 100);
                    return s + pu_n * (l.quantite || 1) * (1 + (l.taux_tva || 0));
                  }, 0) * 100) / 100;
                  totalHT_eur = totalHT;
                  totalTTC_eur = totalTTC;
                }
                setFactureForm({
                  devise: editAchat?.devise_fournisseur || 'EUR',
                  type_facture: nbAcomptes > 0 ? 'solde' : 'standard',
                  montant_ht:      isDeviseMode ? totalHT  : totalHT,
                  montant_ttc:     nbAcomptes > 0 ? Math.max(0, totalTTC - totalAcomptesHT) : totalTTC,
                  montant_ht_eur:  isDeviseMode ? totalHT_eur  : undefined,
                  montant_ttc_eur: isDeviseMode ? totalTTC_eur : undefined,
                  taux_change:     isDeviseMode ? (tauxChange || editAchat?.taux_change_applique || null) : null,
                  montant_acompte_deduit: nbAcomptes > 0 ? totalAcomptesHT : 0,
                });
                setFactureLignes(lignesPreremplies);
              }}
              data-testid="btn-nouvelle-facture"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: 'var(--primary-gradient)' }}>
              <Plus size={12} /> Nouvelle facture
            </button>
          </div>

          {/* Liste des factures — tableau avec expansion inline */}

          {factures.length === 0 && !factureForm ? (
            <div className="text-center py-10 text-sm" style={{ color: '#9CA3AF' }}>
              Aucune facture enregistrée
            </div>
          ) : (factures.length > 0 || factureForm) && (
            <>
            <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,.07), 0 0 0 1px #E2E8F0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E8EDF2' }}>
                    {['N° Facture', 'Date', 'Devise', 'Montant TTC', 'Contre-valeur EUR', 'Opération'].map((h, i) => (
                      <th key={i} style={{ padding: '10px 18px', textAlign: 'center', verticalAlign: 'bottom', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, color: '#374151' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {factures.map((fac) => {
                    const isExpanded = factureForm?.facture_id === fac.facture_id;
                    return (
                      <React.Fragment key={fac.facture_id}>
                      <tr
                        data-testid={`facture-row-${fac.facture_id}`}
                        onClick={() => {
                          if (isExpanded) { setFactureForm(null); setFactureLignes([]); }
                          else {
                            setFactureForm(fac);
                            api.getFactureAchatLignes(fac.facture_id)
                              .then(r => {
                                const taux = fac.taux_change || tauxChange;
                                const lines = (r.data || []).map(l => ({
                                  ...l,
                                  montant_devise: (isDeviseMode && taux > 0)
                                    ? Math.round((l.prix_unitaire_ht || 0) * taux * 100) / 100
                                    : null,
                                }));
                                setFactureLignes(lines);
                              })
                              .catch(() => setFactureLignes([]));
                          }
                        }}
                        style={{ cursor: 'pointer', background: isExpanded ? '#FAFAFA' : 'transparent', transition: 'background .1s', borderBottom: '1px solid #F1F5F9' }}
                        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#FAFAFA'; }}
                        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {/* N° Facture */}
                        <td style={{ padding: '12px 18px', fontSize: 13, whiteSpace: 'nowrap', textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                            {fac.type_facture === 'acompte' && (
                              <span style={{ padding: '2px 7px', borderRadius: 5, fontSize: 11, fontWeight: 700, background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }}>Acompte</span>
                            )}
                            {fac.type_facture === 'solde' && (
                              <span style={{ padding: '2px 7px', borderRadius: 5, fontSize: 11, fontWeight: 700, background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }}>Solde</span>
                            )}
                            {fac.numero || fac.numero_fournisseur || '—'}
                          </div>
                        </td>

                        {/* Date */}
                        <td style={{ padding: '12px 18px', fontSize: 13, whiteSpace: 'nowrap', textAlign: 'center' }}>
                          {fac.date_facture || '—'}
                        </td>

                        {/* Devise */}
                        <td style={{ padding: '12px 18px', fontSize: 13, textAlign: 'center' }}>
                          {fac.devise || 'EUR'}
                        </td>

                        {/* Montant TTC */}
                        <td style={{ padding: '12px 18px', fontSize: 13, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {fac.montant_ttc != null
                            ? `${fac.montant_ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${fac.devise || 'EUR'}`
                            : '—'}
                        </td>

                        {/* Contre-valeur EUR */}
                        <td style={{ padding: '12px 18px', fontSize: 13, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {fac.devise !== 'EUR' && fac.montant_ttc_eur != null
                            ? `${fac.montant_ttc_eur.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`
                            : '—'}
                        </td>

                        {/* Opération */}
                        <td style={{ padding: '12px 18px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {/* Badge bypass CNHI → WIMAT */}
                            {fac.societe_imputation_id && (
                              <span title="Comptabilisée dans WIMAT (bypass exceptionnel CNHI)"
                                style={{ padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700, background: '#FEF9C3', color: '#854D0E', border: '1px solid #FDE047', whiteSpace: 'nowrap' }}>
                                → WIMAT
                              </span>
                            )}
                            {/* PDF */}
                            {fac.fichier_pdf_url
                              ? <a href={fac.fichier_pdf_url} target="_blank" rel="noreferrer"
                                  data-testid={`btn-view-pdf-${fac.facture_id}`}
                                  style={{ padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#FEF3C7', color: '#92400E', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <ExternalLink size={11} /> PDF
                                </a>
                              : !isReadOnly && (
                                <>
                                  <input type="file" accept="application/pdf"
                                    ref={el => { pdfInputRefs.current[fac.facture_id] = el; }}
                                    onChange={e => { const f = e.target.files?.[0]; if (!f) return; e.target.value = ''; handleUploadPdfFacture(fac.facture_id, f); }}
                                    className="hidden" />
                                  <button onClick={() => pdfInputRefs.current[fac.facture_id]?.click()}
                                    disabled={pdfUploading[fac.facture_id]}
                                    data-testid={`btn-upload-pdf-${fac.facture_id}`}
                                    style={{ padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#F3F4F6', color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 4, opacity: pdfUploading[fac.facture_id] ? 0.5 : 1 }}>
                                    {pdfUploading[fac.facture_id] ? <Loader2 size={11} className="animate-spin" /> : <UploadCloud size={11} />}
                                    {pdfUploading[fac.facture_id] ? '…' : 'Joindre PDF'}
                                  </button>
                                </>
                              )
                            }
                            {/* Envoyer vers Odoo ou Pennylane selon provider_comptable */}
                            {!isReadOnly && (
                              achatProvider === 'pennylane'
                                ? !fac.pl_invoice_id
                                : (fac.fichier_pdf_url && !fac.odoo_invoice_id)
                            ) && (
                              <button onClick={() => handleSendToOdoo(fac.facture_id)}
                                disabled={odooSending[fac.facture_id]}
                                data-testid={`btn-send-odoo-${fac.facture_id}`}
                                title={achatProvider === 'pennylane' ? 'Transmettre cette facture à Pennylane' : 'Transmettre cette facture à Odoo'}
                                style={{ padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: achatProvider === 'pennylane' ? '#F0FDF4' : '#EFF6FF', color: achatProvider === 'pennylane' ? '#15803D' : '#1D4ED8', display: 'inline-flex', alignItems: 'center', gap: 4, opacity: odooSending[fac.facture_id] ? 0.5 : 1 }}>
                                {odooSending[fac.facture_id] ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                                {odooSending[fac.facture_id] ? '…' : achatProvider === 'pennylane' ? 'Pennylane' : 'Odoo'}
                              </button>
                            )}
                            {/* Créer BR */}
                            {!isReadOnly && ['commande', 'en_cours_reception', 'receptionne'].includes(editAchat?.stade) && (
                              <button onClick={() => handleCreerBrFromFacture(fac.facture_id)}
                                disabled={brCreating[fac.facture_id]}
                                data-testid={`btn-creer-br-${fac.facture_id}`}
                                style={{ padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#15803D', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 4, opacity: brCreating[fac.facture_id] ? 0.5 : 1 }}>
                                {brCreating[fac.facture_id] ? <Loader2 size={11} className="animate-spin" /> : <PackageCheck size={11} />}
                                {brCreating[fac.facture_id] ? '…' : 'Créer BR'}
                              </button>
                            )}
                            {/* Supprimer */}
                            {!isReadOnly && !fac.pl_invoice_id && (
                              <button onClick={() => handleDeleteFacture(fac.facture_id)}
                                data-testid={`btn-delete-facture-${fac.facture_id}`}
                                title="Supprimer"
                                style={{ padding: '5px 8px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#FEE2E2', color: '#DC2626', display: 'inline-flex', alignItems: 'center' }}>
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* ── Expansion inline ── */}
                      {isExpanded && factureForm && (
                        <tr>
                          <td colSpan={6} style={{ padding: 0 }}>
                            <div className="p-4 space-y-3" data-testid="facture-form"
                              style={{ background: '#FAFAFA', borderTop: '1px solid #E8EDF2' }}>

                              {/* ── Ligne 1 : type + boutons ── */}
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  {[
                                    ...(nbAcomptes===0?[{key:'standard',label:'Standard'}]:[]),
                                    {key:'acompte',label:'Acompte'},
                                    ...(nbAcomptes>0?[{key:'solde',label:'Solde'}]:[]),
                                  ].map(({key,label}) => {
                                    const active=(factureForm.type_facture||'standard')===key;
                                    return (
                                      <button key={key} data-testid={`btn-type-facture-${key}`}
                                        onClick={e=>{ e.stopPropagation();
                                          const linesBase = factureLignes.length>0 ? factureLignes :
                                            (editAchat?.lignes||[]).filter(l=>!['section','texte'].includes(l.type_ligne)).map(l=>({...l,quantite:l.quantite_commandee}));
                                          const brut = linesBase.reduce((s,l)=>{const p=(l.prix_unitaire_ht||0)*(1-(l.remise||0)/100);return s+p*(l.quantite||1);},0);
                                          const netTTC = Math.max(0,Math.round((brut-totalAcomptesHT)*100)/100);
                                          setFactureForm(p=>({...p,type_facture:key,facture_acompte_id:null,
                                            montant_acompte_deduit:key==='solde'?totalAcomptesHT:0,
                                            montant_ttc:key==='solde'?netTTC:p.montant_ttc}));
                                          if(key==='acompte')setFactureLignes([]);
                                          else if(factureLignes.length===0)setFactureLignes((editAchat?.lignes||[]).filter(l=>!['section','texte'].includes(l.type_ligne)).map((l,i)=>({achat_ligne_id:l.ligne_id,article_id:l.article_id,materiel_id:l.materiel_id||null,designation:l.designation||l.article_designation||'',reference:l.article_ref||'',quantite:l.quantite_commandee||1,prix_unitaire_ht:l.prix_unitaire_ht||0,montant_devise:isDeviseMode?(l.montant_devise||null):null,taux_tva:(l.taux_tva!=null?(parseFloat(l.taux_tva)>1?parseFloat(l.taux_tva)/100:parseFloat(l.taux_tva)):0),remise:l.remise||0,compte_charge:l.compte_charge||null,famille_nom:l.famille_nom||null,sequence:i})));
                                        }}
                                        className="px-3 py-1 rounded-lg text-xs font-semibold border"
                                        style={{background:active?(key==='acompte'?'#FEF3C7':key==='solde'?'#F0FDF4':'#EEF2FF'):'#F9FAFB',color:active?(key==='acompte'?'#92400E':key==='solde'?'#15803D':'#4338CA'):'#6B7280',borderColor:active?(key==='acompte'?'#FCD34D':key==='solde'?'#86EFAC':'#A5B4FC'):'#E6E6E1',fontWeight:active?700:500}}>
                                        {label}
                                      </button>
                                    );
                                  })}
                                  {factureForm.type_facture==='acompte'&&(
                                    <div className="flex items-center gap-3 ml-2">
                                      <span className="text-xs" style={{color:'#92400E'}}>TVA</span>
                                      <select value={factureForm.tva_acompte??0} onClick={e=>e.stopPropagation()}
                                        onChange={e=>setFactureForm(p=>({...p,tva_acompte:parseFloat(e.target.value)}))}
                                        className="rounded border px-2 py-0.5 text-xs focus:outline-none" style={{borderColor:'#FCD34D',background:'#FEF3C7',color:'#92400E',minWidth:60}}>
                                        <option value={0}>0 %</option>
                                        <option value={8.5}>8,5 %</option>
                                        <option value={20}>20 %</option>
                                      </select>
                                      {factureForm.montant_ttc>0&&<span className="text-xs font-mono" style={{color:'#92400E'}}>HT : {htAcompte.toLocaleString('fr-FR',{minimumFractionDigits:2})} {factureForm.devise||'EUR'}</span>}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button onClick={e=>{e.stopPropagation();handleSaveFacture();}} disabled={factureSaving||factureLignesSaving}
                                    data-testid="btn-save-facture"
                                    className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                                    style={{background:'var(--primary-gradient)'}}>
                                    {(factureSaving||factureLignesSaving)?<Loader2 size={11} className="animate-spin"/>:<Save size={11}/>}
                                    {factureForm.facture_id?'Enregistrer':'Créer'}
                                  </button>
                                  <button onClick={e=>{e.stopPropagation();setFactureForm(null);setFactureLignes([]);}}
                                    className="px-3 py-1 rounded-lg text-xs font-medium border hover:bg-gray-50" style={{borderColor:'#E6E6E1',color:'#6B7280'}}>
                                    Annuler
                                  </button>
                                </div>
                              </div>

                              {/* ── Grille 3×2 ── */}
                              {(()=>{
                                const lbl={color:'#6B7280',fontSize:'11px',fontWeight:500,whiteSpace:'nowrap'};
                                const lbl2={...lbl,paddingLeft:24};
                                const lbl3={...lbl,paddingLeft:24};
                                const fCls="w-full rounded-lg border px-2.5 py-1.5 text-xs focus:outline-none";
                                const fSt={borderColor:'#E6E6E1'};
                                const roSt={borderColor:'#E6E6E1',color:'#1A1A1A',background:'#FAFAFA'};
                                const htVal=factureLignes.length>0?totalFactureEurHT:(factureForm.montant_ht_eur??0);
                                const ttcVal=factureLignes.length>0?totalFactureEurTTC:(factureForm.montant_ttc_eur??0);
                                return (
                                  <div style={{display:'grid',gridTemplateColumns:'max-content 1fr max-content 1fr max-content 1fr',columnGap:8,rowGap:10,alignItems:'center'}}>
                                    {/* Ligne 1 */}
                                    <span style={lbl}>N° facture</span>
                                    <input value={factureForm.numero_fournisseur||''} data-testid="input-numero-fournisseur" placeholder="INV-2026-001"
                                      onChange={e=>setFactureForm(p=>({...p,numero_fournisseur:e.target.value}))}
                                      className={fCls+' focus:ring-1'} style={fSt}/>
                                    <span style={lbl2}>Devise</span>
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <select value={factureForm.devise||'EUR'} data-testid="select-devise-facture"
                                        onChange={e=>handleFactureFieldChange('devise',e.target.value)}
                                        className="flex-1 min-w-0 rounded-lg border px-2 py-1.5 text-xs focus:outline-none" style={fSt}>
                                        <option value="EUR">EUR</option><option value="USD">USD</option>
                                      </select>
                                      {isDeviseMode&&<span className="text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0" style={{background:'#F3F0FF',color:'#7C3AED'}}>
                                        {factureTauxLoading?'…':factureTauxJour?`1 ${factureForm.devise}=${(1/factureTauxJour).toFixed(4)} EUR`:'—'}
                                      </span>}
                                    </div>
                                    <span style={lbl3}>{isDeviseMode?'HT EUR':'Montant HT'}</span>
                                    {isDeviseMode
                                      ?<div className={fCls+' text-right font-semibold'} style={roSt}>{htVal.toLocaleString('fr-FR',{minimumFractionDigits:2})} €</div>
                                      :<NumericInput value={factureForm.montant_ht??''} nbDecimals={2} onChange={v=>handleFactureFieldChange('montant_ht',parseFloat(v)||null)} className={fCls} style={fSt}/>
                                    }
                                    {/* Ligne 2 */}
                                    <span style={lbl}>Date</span>
                                    <input type="date" value={factureForm.date_facture||''} data-testid="input-date-facture"
                                      onChange={e=>handleFactureFieldChange('date_facture',e.target.value)}
                                      className={fCls} style={fSt}/>
                                    {isDeviseMode?<><span style={lbl2}>Total TTC {factureForm.devise}</span>
                                      <div className={fCls+' text-right font-semibold'} style={{borderColor:'#C4B5FD',color:'#7C3AED',background:'#FAFAFA'}}>
                                        {totalFactureDeviseTTC.toLocaleString('fr-FR',{minimumFractionDigits:2})} {factureForm.devise}
                                      </div></>
                                    :<><span style={lbl2}>{factureForm.type_facture==='solde'?'Net TTC':'Montant TTC'}</span>
                                      <NumericInput value={factureForm.montant_ttc??''} nbDecimals={2} data-testid="input-montant-ttc" onChange={v=>handleFactureFieldChange('montant_ttc',parseFloat(v)||null)} className={fCls} style={fSt}/>
                                    </>}
                                    <span style={lbl3}>{isDeviseMode?'TTC EUR':'Montant TTC'}</span>
                                    {isDeviseMode
                                      ?<div className={fCls+' text-right font-semibold'} style={roSt}>{ttcVal.toLocaleString('fr-FR',{minimumFractionDigits:2})} €</div>
                                      :<NumericInput value={factureForm.montant_ttc??''} nbDecimals={2} onChange={v=>handleFactureFieldChange('montant_ttc',parseFloat(v)||null)} className={fCls} style={fSt}/>
                                    }
                                    {/* Récap Solde */}
                                    {factureForm.type_facture==='solde'&&<div style={{gridColumn:'1 / -1'}}>
                                      <div className="rounded-lg px-3 py-2 text-xs space-y-1" style={{background:'#DCFCE7',border:'1px solid #86EFAC'}}>
                                        {nbAcomptes===0?<div style={{color:'#B45309'}}>Saisissez d'abord une facture d'acompte.</div>:<>
                                          <div className="flex justify-between"><span style={{color:'#166534'}}>Valeur brute</span><strong style={{color:'#166534'}}>{totalLignesHT.toLocaleString('fr-FR',{minimumFractionDigits:2})} HT</strong></div>
                                          <div className="flex justify-between"><span style={{color:'#166534'}}>Acompte(s) ({nbAcomptes})</span><strong style={{color:'#166534'}}>− {totalAcomptesHT.toLocaleString('fr-FR',{minimumFractionDigits:2})}</strong></div>
                                          <div className="flex justify-between border-t pt-0.5" style={{borderColor:'#86EFAC'}}><span style={{color:'#166534',fontWeight:700}}>Facture finale</span><strong style={{color:'#166534'}}>{Math.max(0,totalLignesHT-totalAcomptesHT).toLocaleString('fr-FR',{minimumFractionDigits:2})}</strong></div>
                                        </>}
                                      </div>
                                    </div>}
                                  </div>
                                );
                              })()}

                              {/* Lignes articles (sauf acompte) */}
                              {factureForm.type_facture!=='acompte'&&(
                                <div onClick={e=>e.stopPropagation()}>
                                  <FactureLignesTable lignes={factureLignes} setLignes={setFactureLignes} orgId={editAchat?.org_id}
                                    isDeviseMode={isDeviseMode} devise={fournisseurDevise} tauxChange={factureTauxJour}/>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}

                  {/* Ligne "nouvelle facture" en bas du tableau */}
                  {factureForm && !factureForm.facture_id && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0, borderTop: '1px solid #E8EDF2' }}>
                        <div className="p-4 space-y-3" data-testid="facture-form" style={{ background: '#FAFAFA', borderTop: '1px solid #E8EDF2' }}>

                          {/* Ligne 1 : type + boutons */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {[
                                ...(nbAcomptes===0?[{key:'standard',label:'Standard'}]:[]),
                                {key:'acompte',label:'Acompte'},
                                ...(nbAcomptes>0?[{key:'solde',label:'Solde'}]:[]),
                              ].map(({key,label}) => {
                                const active=(factureForm.type_facture||'standard')===key;
                                return (
                                  <button key={key} data-testid={`btn-type-facture-${key}`}
                                    onClick={()=>{
                                      const brut=(editAchat?.lignes||[]).filter(l=>!['section','texte'].includes(l.type_ligne)).reduce((s,l)=>{const p=(l.prix_unitaire_ht||0)*(1-(l.remise||0)/100);return s+p*(l.quantite_commandee||1);},0);
                                      const netTTC=Math.max(0,Math.round((brut-totalAcomptesHT)*100)/100);
                                      setFactureForm(p=>({...p,type_facture:key,facture_acompte_id:null,montant_acompte_deduit:key==='solde'?totalAcomptesHT:0,montant_ttc:key==='solde'?netTTC:p.montant_ttc}));
                                      if(key==='acompte')setFactureLignes([]);
                                      else if(factureLignes.length===0)setFactureLignes((editAchat?.lignes||[]).filter(l=>!['section','texte'].includes(l.type_ligne)).map((l,i)=>({achat_ligne_id:l.ligne_id,article_id:l.article_id,materiel_id:l.materiel_id||null,designation:l.designation||l.article_designation||'',reference:l.article_ref||'',quantite:l.quantite_commandee||1,prix_unitaire_ht:l.prix_unitaire_ht||0,montant_devise:isDeviseMode?(l.montant_devise||null):null,taux_tva:(l.taux_tva!=null?(parseFloat(l.taux_tva)>1?parseFloat(l.taux_tva)/100:parseFloat(l.taux_tva)):0),remise:l.remise||0,compte_charge:l.compte_charge||null,famille_nom:l.famille_nom||null,sequence:i})));
                                    }}
                                    className="px-3 py-1 rounded-lg text-xs font-semibold border"
                                    style={{background:active?(key==='acompte'?'#FEF3C7':key==='solde'?'#F0FDF4':'#EEF2FF'):'#F9FAFB',color:active?(key==='acompte'?'#92400E':key==='solde'?'#15803D':'#4338CA'):'#6B7280',borderColor:active?(key==='acompte'?'#FCD34D':key==='solde'?'#86EFAC':'#A5B4FC'):'#E6E6E1',fontWeight:active?700:500}}>
                                    {label}
                                  </button>
                                );
                              })}
                              {factureForm.type_facture==='acompte'&&<span className="text-xs" style={{color:'#92400E'}}>Purement financier</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={handleSaveFacture} disabled={factureSaving||factureLignesSaving}
                                data-testid="btn-save-facture"
                                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                                style={{background:'var(--primary-gradient)'}}>
                                {(factureSaving||factureLignesSaving)?<Loader2 size={11} className="animate-spin"/>:<Save size={11}/>}
                                Créer
                              </button>
                              <button onClick={()=>{setFactureForm(null);setFactureLignes([]);}}
                                className="px-3 py-1 rounded-lg text-xs font-medium border hover:bg-gray-50" style={{borderColor:'#E6E6E1',color:'#6B7280'}}>
                                Annuler
                              </button>
                            </div>
                          </div>

                          {/* Grille 3×2 */}
                          {(()=>{
                            const lbl={color:'#6B7280',fontSize:'11px',fontWeight:500,whiteSpace:'nowrap'};
                            const lbl2={...lbl,paddingLeft:24};
                            const lbl3={...lbl,paddingLeft:24};
                            const fCls="w-full rounded-lg border px-2.5 py-1.5 text-xs focus:outline-none";
                            const fSt={borderColor:'#E6E6E1'};
                            const roSt={borderColor:'#E6E6E1',color:'#1A1A1A',background:'#FAFAFA'};
                            const htVal=factureLignes.length>0?totalFactureEurHT:(factureForm.montant_ht_eur??0);
                            const ttcVal=factureLignes.length>0?totalFactureEurTTC:(factureForm.montant_ttc_eur??0);
                            return (
                              <div style={{display:'grid',gridTemplateColumns:'max-content 1fr max-content 1fr max-content 1fr',columnGap:8,rowGap:10,alignItems:'center'}}>
                                <span style={lbl}>N° facture</span>
                                <input value={factureForm.numero_fournisseur||''} data-testid="input-numero-fournisseur" placeholder="INV-2026-001"
                                  onChange={e=>setFactureForm(p=>({...p,numero_fournisseur:e.target.value}))}
                                  className={fCls+' focus:ring-1'} style={fSt}/>
                                <span style={lbl2}>Devise</span>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <select value={factureForm.devise||'EUR'} data-testid="select-devise-facture"
                                    onChange={e=>handleFactureFieldChange('devise',e.target.value)}
                                    className="flex-1 min-w-0 rounded-lg border px-2 py-1.5 text-xs focus:outline-none" style={fSt}>
                                    <option value="EUR">EUR</option><option value="USD">USD</option>
                                  </select>
                                  {isDeviseMode&&<span className="text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0" style={{background:'#F3F0FF',color:'#7C3AED'}}>
                                    {factureTauxLoading?'…':factureTauxJour?`1 ${factureForm.devise}=${(1/factureTauxJour).toFixed(4)} EUR`:'—'}
                                  </span>}
                                </div>
                                <span style={lbl3}>{isDeviseMode?'HT EUR':'Montant HT'}</span>
                                {isDeviseMode
                                  ?<div className={fCls+' text-right font-semibold'} style={roSt}>{htVal.toLocaleString('fr-FR',{minimumFractionDigits:2})} €</div>
                                  :<NumericInput value={factureForm.montant_ht??''} nbDecimals={2} onChange={v=>handleFactureFieldChange('montant_ht',parseFloat(v)||null)} className={fCls} style={fSt}/>
                                }
                                <span style={lbl}>Date</span>
                                <input type="date" value={factureForm.date_facture||''} data-testid="input-date-facture"
                                  onChange={e=>handleFactureFieldChange('date_facture',e.target.value)}
                                  className={fCls} style={fSt}/>
                                {isDeviseMode?<><span style={lbl2}>Total TTC {factureForm.devise}</span>
                                  <div className={fCls+' text-right font-semibold'} style={{borderColor:'#C4B5FD',color:'#7C3AED',background:'#FAFAFA'}}>
                                    {totalFactureDeviseTTC.toLocaleString('fr-FR',{minimumFractionDigits:2})} {factureForm.devise}
                                  </div></>
                                :<><span style={lbl2}>{factureForm.type_facture==='solde'?'Net TTC':'Montant TTC'}</span>
                                  <NumericInput value={factureForm.montant_ttc??''} nbDecimals={2} data-testid="input-montant-ttc" onChange={v=>handleFactureFieldChange('montant_ttc',parseFloat(v)||null)} className={fCls} style={fSt}/>
                                </>}
                                <span style={lbl3}>{isDeviseMode?'TTC EUR':'Montant TTC'}</span>
                                {isDeviseMode
                                  ?<div className={fCls+' text-right font-semibold'} style={roSt}>{ttcVal.toLocaleString('fr-FR',{minimumFractionDigits:2})} €</div>
                                  :<NumericInput value={factureForm.montant_ttc??''} nbDecimals={2} onChange={v=>handleFactureFieldChange('montant_ttc',parseFloat(v)||null)} className={fCls} style={fSt}/>
                                }
                                {factureForm.type_facture==='solde'&&<div style={{gridColumn:'1 / -1'}}>
                                  <div className="rounded-lg px-3 py-2 text-xs space-y-1" style={{background:'#DCFCE7',border:'1px solid #86EFAC'}}>
                                    {nbAcomptes===0?<div style={{color:'#B45309'}}>Saisissez d'abord une facture d'acompte.</div>:<>
                                      <div className="flex justify-between"><span style={{color:'#166534'}}>Valeur brute</span><strong style={{color:'#166534'}}>{totalLignesHT.toLocaleString('fr-FR',{minimumFractionDigits:2})} HT</strong></div>
                                      <div className="flex justify-between"><span style={{color:'#166534'}}>Acompte(s) ({nbAcomptes})</span><strong style={{color:'#166534'}}>− {totalAcomptesHT.toLocaleString('fr-FR',{minimumFractionDigits:2})}</strong></div>
                                      <div className="flex justify-between border-t pt-0.5" style={{borderColor:'#86EFAC'}}><span style={{color:'#166534',fontWeight:700}}>Facture finale</span><strong style={{color:'#166534'}}>{Math.max(0,totalLignesHT-totalAcomptesHT).toLocaleString('fr-FR',{minimumFractionDigits:2})}</strong></div>
                                    </>}
                                  </div>
                                </div>}
                              </div>
                            );
                          })()}

                          {/* Lignes articles (sauf acompte) */}
                          {factureForm.type_facture!=='acompte'&&(
                            <FactureLignesTable lignes={factureLignes} setLignes={setFactureLignes} orgId={editAchat?.org_id}
                              isDeviseMode={isDeviseMode} devise={fournisseurDevise} tauxChange={factureTauxJour}/>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Récapitulatif Acompte / Solde ── */}
            {(() => {
              const acomptes = factures.filter(f => f.type_facture === 'acompte');
              const soldes   = factures.filter(f => f.type_facture === 'solde');
              if (acomptes.length === 0 && soldes.length === 0) return null;
              const totalAcompte = acomptes.reduce((s, f) => s + parseFloat(f.montant_ttc || f.montant_ht || 0), 0);
              const totalSolde   = soldes.reduce((s, f) => s + parseFloat(f.montant_ttc || 0), 0);
              const totalBrut    = totalAcompte + totalSolde;
              const devises = [...new Set(factures.map(f => f.devise || 'EUR'))];
              const fmt = n => parseFloat(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 });
              return (
                <div className="mt-3 rounded-xl px-4 py-3 flex items-center gap-4 flex-wrap text-xs"
                  style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                  {acomptes.length > 0 && (
                    <span style={{ color: '#92400E' }}>
                      Acompte : <strong>{fmt(totalAcompte)} {devises[0]}</strong>
                    </span>
                  )}
                  {acomptes.length > 0 && soldes.length > 0 && <span style={{ color: '#9CA3AF' }}>+</span>}
                  {soldes.length > 0 && (
                    <span style={{ color: '#15803D' }}>
                      Solde : <strong>{fmt(totalSolde)} {devises[0]}</strong>
                    </span>
                  )}
                  {(acomptes.length > 0 && soldes.length > 0) && (
                    <>
                      <span style={{ color: '#9CA3AF' }}>=</span>
                      <span style={{ color: '#1D4ED8', fontWeight: 700, fontSize: 13 }}>
                        Total commande : {fmt(totalBrut)} {devises[0]} HT
                      </span>
                    </>
                  )}
                </div>
              );
            })()}
            </>
          )}

          {/* ── Récap écart de change (indicatif — ne pas utiliser en comptabilité) ── */}
          {isDeviseMode && editAchat?.taux_change_applique && factures.some(f => f.montant_ttc_eur != null) && (() => {
            const tauxCmd   = editAchat.taux_change_applique;
            const ttcEurCmd = parseFloat(editAchat.total_ttc || 0);
            const ttcDevCmd = Math.round(ttcEurCmd * tauxCmd * 100) / 100;
            const facDev    = factures.filter(f => f.devise !== 'EUR' && f.montant_ttc_eur != null);
            const totalFacDev = facDev.reduce((s, f) => s + parseFloat(f.montant_ttc    || 0), 0);
            const totalFacEur = facDev.reduce((s, f) => s + parseFloat(f.montant_ttc_eur || 0), 0);
            const ecart = totalFacEur - ttcEurCmd;
            const ecartLabel = Math.abs(ecart) < 0.005 ? "Pas d'écart" : ecart > 0 ? 'Perte de change' : 'Gain de change';
            const ecartColor = Math.abs(ecart) < 0.005 ? '#6B7280' : ecart > 0 ? '#DC2626' : '#16A34A';
            const fmtN  = n => parseFloat(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const fmtTx = t => t ? (1 / t).toFixed(4) : '—';
            const fmtDt = d => d ? d.split('-').reverse().join('/') : '—';
            return (
              <div style={{ marginTop: 14 }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#6B7280' }}>
                  Écart de change — indicatif uniquement
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#374151' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                      {['', `TTC (${fournisseurDevise})`, `1 ${fournisseurDevise} = X €`, 'TTC (€)'].map((h, i) => (
                        <th key={i} style={{ padding: '3px 6px', textAlign: i === 0 ? 'left' : 'right', color: '#6B7280', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '3px 6px' }}>Commande ({fmtDt(editAchat.date_commande)})</td>
                      <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtN(ttcDevCmd)}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtTx(tauxCmd)}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>{fmtN(ttcEurCmd)}</td>
                    </tr>
                    {facDev.map(fac => (
                      <tr key={fac.facture_id}>
                        <td style={{ padding: '3px 6px' }}>Facture {fac.numero_fournisseur || ('…' + (fac.facture_id || '').slice(-5))} ({fmtDt(fac.date_facture)})</td>
                        <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtN(fac.montant_ttc)}</td>
                        <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtTx(fac.taux_change)}</td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>{fmtN(fac.montant_ttc_eur)}</td>
                      </tr>
                    ))}
                    {facDev.length > 1 && (
                      <tr style={{ borderTop: '1px solid #E2E8F0' }}>
                        <td style={{ padding: '3px 6px', fontWeight: 600 }}>Total factures</td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>{fmtN(totalFacDev)}</td>
                        <td></td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>{fmtN(totalFacEur)}</td>
                      </tr>
                    )}
                    <tr style={{ borderTop: '1px solid #E2E8F0' }}>
                      <td style={{ padding: '3px 6px', fontWeight: 700, color: ecartColor }}>{ecartLabel}</td>
                      <td></td><td></td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 700, color: ecartColor }}>
                        {ecart >= 0 ? '+' : ''}{fmtN(ecart)} €
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}

        </div>
      )}

      {/* ── Onglet Détails ── */}
      {(activeTab === 'details' || !editAchat) && (
        <div className="space-y-6">
          {rappelBanner && (
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border text-sm font-medium"
              style={{ background: '#EFF6FF', borderColor: '#BFDBFE', color: '#1D4ED8' }}>
              <RotateCcw size={14} />
              Version V{rappelBanner.numero_revision} chargée — enregistrez pour confirmer
              <button onClick={() => setRappelBanner(null)} className="ml-auto opacity-60 hover:opacity-100"><X size={13} /></button>
            </div>
          )}
          {/* Section Fournisseur & Informations */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#6B7280' }}>Fournisseur & Informations</h3>
            <div className="grid grid-cols-3 gap-x-10 gap-y-3">

              {/* Col 1 : Fournisseur + Interlocuteur */}
              <div className="flex flex-col gap-3">
                {/* Fournisseur async search */}
                <div ref={fornRef} className="flex items-center gap-2">
                  <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 72 }}>Fournisseur</label>
                  <div className="flex-1 relative" style={{ minWidth: 0 }}>
                    {form.fournisseur_id ? (
                      <div className="flex items-center justify-between gap-1 rounded-lg border px-2" style={{ ...inpRow, height: 30, borderColor: '#E6E6E1' }}>
                        <span className="font-semibold text-xs truncate" style={{ color: 'var(--primary-solid)' }}>{form.fournisseur_nom}</span>
                        {!isReadOnly && (
                          <button type="button" onClick={() => {
                            setField('fournisseur_id', ''); setField('fournisseur_nom', '');
                            setField('contact_id', ''); setField('contact_nom', '');
                            setFornQuery(''); setContacts([]);
                          }}
                            className="flex-shrink-0 text-xs px-1 rounded hover:bg-red-50 transition-colors"
                            style={{ color: '#DC2626', border: '1px solid #FCA5A5', lineHeight: '16px' }}>✕</button>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 w-full rounded-lg border px-2 focus-within:ring-1 focus-within:ring-blue-400"
                          style={{ ...inpRow, height: 30, borderColor: '#E6E6E1' }}>
                          <Search size={11} style={{ color: '#9CA3AF', flexShrink: 0 }} />
                          <input value={fornQuery}
                            onChange={e => { setFornQuery(e.target.value); setFornOpen(true); }}
                            onFocus={() => setFornOpen(true)}
                            disabled={isReadOnly}
                            placeholder="Rechercher un fournisseur…"
                            className="flex-1 text-xs bg-transparent focus:outline-none"
                            style={{ color: '#1A1A1A' }}
                            data-testid="achat-fournisseur-search" />
                        </div>
                        {fornOpen && fornResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 rounded-xl border bg-white shadow-xl overflow-hidden" style={{ borderColor: '#E6E6E1', top: '100%', left: 0 }}>
                            {fornResults.map(f => (
                              <button key={f.entreprise_id} type="button"
                                onMouseDown={() => {
                                  setField('fournisseur_id', f.entreprise_id);
                                  setField('fournisseur_nom', f.nom);
                                  setSelectedFournisseurDevise(f.devise || 'EUR');
                                  setField('contact_id', ''); setField('contact_nom', '');
                                  setFornQuery(''); setFornOpen(false);
                                  checkSiren(f);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors text-xs flex items-center gap-2"
                                style={{ borderBottom: '1px solid #F2F2EE' }}>
                                <span className="font-semibold truncate" style={{ color: '#111827' }}>{f.nom}</span>
                                {f.ville && <span style={{ color: '#9CA3AF' }}>{f.ville}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Interlocuteur */}
                {form.fournisseur_id && (
                  <div ref={contactRef} className="flex items-center gap-2">
                    <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 72 }}>Contact</label>
                    <div className="flex-1 relative" style={{ minWidth: 0 }}>
                      {form.contact_id ? (
                        <div className="flex items-center justify-between gap-1 rounded-lg border px-2" style={{ ...inpRow, height: 30, borderColor: '#E6E6E1' }}>
                          <span className="text-xs truncate" style={{ color: '#1A1A1A' }}>{form.contact_nom}</span>
                          {!isReadOnly && (
                            <button type="button" onClick={() => { setField('contact_id', ''); setField('contact_nom', ''); }}
                              className="flex-shrink-0 text-xs px-1 rounded hover:bg-red-50 transition-colors"
                              style={{ color: '#DC2626', border: '1px solid #FCA5A5', lineHeight: '16px' }}>✕</button>
                          )}
                        </div>
                      ) : (
                        <>
                          <button type="button" onClick={() => setContactDropOpen(v => !v)} disabled={isReadOnly}
                            className="w-full flex items-center justify-between gap-1 rounded-lg border px-2 text-xs text-left"
                            style={{ ...inpRow, height: 30, borderColor: '#E6E6E1', color: '#9CA3AF' }}
                            data-testid="achat-contact-btn">
                            {contacts.length === 0 ? '— Aucun contact —' : '— Sélectionner un contact —'}
                            <ChevronDown size={11} />
                          </button>
                          {contactDropOpen && contacts.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 rounded-xl border bg-white shadow-xl overflow-hidden" style={{ borderColor: '#E6E6E1', top: '100%', left: 0 }}>
                              {contacts.map(c => (
                                <button key={c.personne_id || c.contact_id} type="button"
                                  onMouseDown={() => {
                                    setField('contact_id', c.personne_id || c.contact_id);
                                    setField('contact_nom', `${c.prenom || ''} ${c.nom || ''}`.trim() || c.email || '');
                                    setContactDropOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors text-xs"
                                  style={{ borderBottom: '1px solid #F2F2EE' }}>
                                  <span className="font-semibold" style={{ color: '#111827' }}>
                                    {`${c.prenom || ''} ${c.nom || ''}`.trim() || c.email}
                                  </span>
                                  {c.poste && <span className="ml-1" style={{ color: '#9CA3AF' }}>· {c.poste}</span>}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Col 2 : Titre + Réf. fournisseur + Conditions de paiement */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Titre</label>
                  <input value={form.titre} disabled={isReadOnly}
                    onChange={e => setField('titre', e.target.value)}
                    placeholder="Titre ou objet de la commande…"
                    data-testid="achat-titre"
                    className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                    style={{ ...inpRow, height: 30, borderColor: '#E6E6E1' }} />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Réf. fourn.</label>
                  <input value={form.ref_fournisseur} disabled={isReadOnly}
                    onChange={e => setField('ref_fournisseur', e.target.value)}
                    placeholder="N° commande fournisseur…"
                    data-testid="achat-ref-fournisseur"
                    className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                    style={{ ...inpRow, height: 30, borderColor: '#E6E6E1' }} />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Conditions</label>
                  <input value={form.conditions_paiement} disabled={isReadOnly}
                    onChange={e => setField('conditions_paiement', e.target.value)}
                    placeholder="30 jours net, comptant…"
                    data-testid="achat-conditions-paiement"
                    className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                    style={{ ...inpRow, height: 30, borderColor: '#E6E6E1' }} />
                </div>
                {editAchat?.numero_facture_fournisseur && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>N° facture</label>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#F5F3FF', color: '#7C3AED' }}>
                      {editAchat.numero_facture_fournisseur}
                    </span>
                  </div>
                )}
                {editAchat?.odoo_sync_status === 'introuvable_odoo' && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold self-start" style={{ background: '#FEF2F2', color: '#DC2626' }}>
                    Introuvable dans Odoo
                  </span>
                )}
              </div>

              {/* Col 3 : Dates */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Date cmd</label>
                  <input type="date" value={form.date_commande} disabled={isReadOnly}
                    onChange={e => setField('date_commande', e.target.value)}
                    data-testid="achat-date-commande"
                    className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                    style={{ ...inpRow, height: 30, borderColor: '#E6E6E1' }} />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Livraison</label>
                  <input type="date" value={form.date_livraison_prevue} disabled={isReadOnly}
                    onChange={e => setField('date_livraison_prevue', e.target.value)}
                    data-testid="achat-date-livraison"
                    className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                    style={{ ...inpRow, height: 30, borderColor: '#E6E6E1' }} />
                </div>
              </div>
            </div>
          </section>

          {/* Section Lignes */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#6B7280' }}>
              Articles ({form.lignes.length})
            </h3>
            <div className="rounded-xl border" style={{ borderColor: '#E6E6E1' }}>
              <div className="overflow-x-auto">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={form.lignes.map(l => l._key)} strategy={verticalListSortingStrategy}>
                    <table className="w-full">
                      <thead>
                        <tr style={{ background: '#FAFAF8', borderBottom: '1px solid #E6E6E1' }}>
                          {!isReadOnly && <th style={{ width: 28 }} />}
                          <th className="px-2 py-2 text-xs font-semibold uppercase text-left" style={{ color: '#6B7280', width: 180 }}>Référence</th>
                          <th className="px-2 py-2 text-xs font-semibold uppercase text-left" style={{ color: '#6B7280' }}>Désignation</th>
                          <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 70 }}>Qté</th>
                          {isDeviseMode && (
                            <th className="px-2 py-2 text-xs font-semibold uppercase text-right" style={{ color: '#7C3AED', width: 110 }}>
                              Montant {fournisseurDevise}
                              {tauxChange && <span className="ml-1 font-normal normal-case" style={{ color: '#9CA3AF' }}>({tauxChange.toFixed(4)})</span>}
                            </th>
                          )}
                          <th className="px-2 py-2 text-xs font-semibold uppercase text-right" style={{ color: '#6B7280', width: 100 }}>P.U. HT</th>
                          <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 80 }}>TVA</th>
                          <th className="px-2 py-2 text-xs font-semibold uppercase text-right" style={{ color: '#6B7280', width: 100 }}>Total HT</th>
                          {!isReadOnly && <th style={{ width: 36 }} />}
                        </tr>
                      </thead>
                      <tbody>
                        {form.lignes.length === 0 && isReadOnly ? (
                          <tr><td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: '#6B7280' }}>Aucune ligne</td></tr>
                        ) : (
                          form.lignes.map(ligne => (
                            <LigneAchatRow key={ligne._key} id={ligne._key} ligne={ligne}
                              onChangeLigne={updateLigne} onDeleteLigne={removeLigne}
                              tvaRates={resolvedTvaRates} readOnly={isReadOnly}
                              uniteDecimals={uniteDecimals}
                              isDeviseMode={isDeviseMode} fournisseurDevise={fournisseurDevise} tauxChange={tauxChange} />
                          ))
                        )}
                        {!isReadOnly && <GhostRowAchat onAdd={addLigneFromArticle} />}
                      </tbody>
                    </table>
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          </section>

          {/* Section Notes */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#6B7280' }}>Notes / Conditions</h3>
            <textarea value={form.notes || ''} disabled={isReadOnly}
              onChange={e => { setField('notes', e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
              rows={3} placeholder="Remarques, conditions de livraison…"
              className="w-full text-sm px-3 py-2 rounded-lg border focus:outline-none"
              style={{ borderColor: '#E6E6E1', background: '#FFFFFF', color: '#1A1A1A', resize: 'none', overflow: 'hidden', minHeight: '72px' }} />
          </section>
        </div>
      )}

      {/* ── Onglet Échéances ── */}
      {activeTab === 'echeances' && editAchat && (
        <EcheancesTab
          docId={editAchat.achat_id}
          totalTTC={editAchat?.total_ttc}
          fetchFn={api.getAchatEcheances}
          createFn={api.createAchatEcheance}
          updateFn={api.updateAchatEcheance}
          deleteFn={api.deleteAchatEcheance}
          readOnly={editAchat?.stade === 'annule'}
        />
      )}

      {/* ── Onglet Révisions ── */}
      {activeTab === 'revisions' && editAchat && (
        <div className="space-y-3">
          {loadingTab && <div className="flex items-center gap-2 text-sm py-6" style={{ color: '#6B7280' }}><Loader2 size={14} className="animate-spin" /> Chargement…</div>}
          {!loadingTab && revisions.length === 0 && (
            <div className="py-10 text-center">
              <RotateCcw size={28} className="mx-auto mb-3" style={{ color: '#D1D5DB' }} />
              <p className="text-sm" style={{ color: '#9CA3AF' }}>Aucune révision enregistrée.</p>
              <p className="text-xs mt-1" style={{ color: '#D1D5DB' }}>Les révisions sont créées automatiquement à chaque sauvegarde.</p>
            </div>
          )}
          {revisions.map(rev => (
            <div key={rev.revision_id} className="rounded-xl border p-4 flex items-start justify-between gap-4"
              style={{ borderColor: '#E6E6E1' }}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold" style={{ color: '#111827' }}>{rev.label || `Révision ${rev.numero_revision}`}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#EEF2FF', color: '#4F46E5' }}>v{rev.numero_revision}</span>
                </div>
                <div className="text-xs" style={{ color: '#6B7280' }}>
                  Par {rev.created_by_nom || '—'} · {_fmtDatetime(rev.created_at)}
                </div>
                <div className="flex gap-3 mt-2 text-xs" style={{ color: '#374151' }}>
                  <span>HT : <strong>{(rev.total_ht ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></span>
                  <span>TVA : {(rev.total_tva ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  <span>TTC : <strong>{(rev.total_ttc ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></span>
                </div>
              </div>
              <div>
                {rappelConfirm === rev.revision_id ? (
                  <div className="flex gap-2">
                    <button onClick={() => handleRappeler(rev.revision_id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ background: '#1D4ED8' }}>Confirmer</button>
                    <button onClick={() => setRappelConfirm(null)}
                      className="px-3 py-1.5 rounded-lg text-xs border" style={{ borderColor: '#E6E6E1' }}>Annuler</button>
                  </div>
                ) : (
                  <button onClick={() => setRappelConfirm(rev.revision_id)} data-testid={`rappeler-${rev.revision_id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border hover:bg-blue-50 transition-colors"
                    style={{ borderColor: '#BFDBFE', color: '#1D4ED8' }}>
                    <RotateCcw size={11} />Rappeler
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}


      {/* ── Onglet Réceptions ── */}
      {activeTab === 'receptions' && editAchat && (
        <div style={{ padding: '12px 0' }}>
          {loadingBRs ? (
            <div className="flex items-center gap-2 text-sm py-6" style={{ color: '#6B7280' }}>
              <Loader2 size={14} className="animate-spin" />Chargement…
            </div>
          ) : brs.length === 0 ? (
            <div className="py-10 text-center">
              <PackageCheck size={28} className="mx-auto mb-3" style={{ color: '#D1D5DB' }} />
              <p className="text-sm" style={{ color: '#9CA3AF' }}>Aucun bon de réception pour cette commande.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {brs.map(br => (
                <div key={br.br_id} className="flex items-center justify-between rounded-xl border px-4 py-3"
                  style={{ borderColor: '#E6E6E1', background: 'var(--surface)' }}>
                  <div className="flex items-center gap-3">
                    <PackageCheck size={15} style={{ color: '#16A34A' }} />
                    <div>
                      <span className="font-semibold text-sm" style={{ color: '#111827' }}>{br.numero}</span>
                      <span className="mx-2 text-xs" style={{ color: '#D1D5DB' }}>·</span>
                      <span className="text-xs" style={{ color: '#6B7280' }}>{br.stock_nom || '—'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs" style={{ color: '#9CA3AF' }}>
                      {br.date_validation ? new Date(br.date_validation).toLocaleDateString('fr-FR') : br.created_at ? new Date(br.created_at).toLocaleDateString('fr-FR') : '—'}
                    </span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${br.stade === 'valide' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                      {br.stade === 'valide' ? 'Validé' : br.stade === 'brouillon' ? 'Brouillon' : br.stade}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Onglet Documents ── */}
      {activeTab === 'documents' && editAchat && (
        <div style={{ padding: '12px 0', height: '100%' }}>
          <DocumentsTab
            entityId={editAchat.achat_id}
            listFn={() => api.getAchatDocuments(editAchat.achat_id)}
            uploadFn={(fd) => api.uploadAchatDocument(editAchat.achat_id, fd)}
            deleteFn={(docId) => api.deleteAchatDocument(editAchat.achat_id, docId)}
            updateFn={(docId, data) => api.patchAchatDocument(editAchat.achat_id, docId, data)}
            downloadFn={(docId) => api.downloadAchatDocument(editAchat.achat_id, docId)}
            onCountChange={setDocCount}
          />
        </div>
      )}

      {/* ── Onglet Activités ── */}
      {activeTab === 'activites' && editAchat?.achat_id && (
        <div style={{ padding: '12px 0' }}>
          <ActivitesFichePanel
            record_type="achat"
            record_id={editAchat.achat_id}
            record_label={editAchat.numero}
            type_document="achat"
            numero_document={editAchat.numero}
            activiteFetcher={() => api.getAchatActivite(editAchat.achat_id).then(r => r.data || [])}
            created_by_nom={editAchat.created_by_nom ?? null}
          />
        </div>
      )}

      {/* ── Modal Email ────────────────────────────────────────────────────────── */}
      {showMailModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" style={{ maxHeight: '90vh', overflow: 'auto' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#E6E6E1' }}>
              <div className="flex items-center gap-2">
                <Mail size={16} style={{ color: '#1D4ED8' }} />
                <span className="font-semibold text-sm" style={{ color: '#111827' }}>
                  {['devis', 'brouillon', 'devis_envoye'].includes(editAchat?.stade) ? 'Envoyer la demande de prix' : 'Envoyer le bon de commande'}
                </span>
              </div>
              <button onClick={() => setShowMailModal(false)} className="p-1 rounded hover:bg-gray-100">
                <X size={16} />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Chips destinataires */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                  Destinataires *
                </label>
                <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border min-h-[38px]"
                  style={{ borderColor: '#E6E6E1' }}>
                  {mailChips.map((chip, i) => (
                    <span key={i} className="flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-medium"
                      style={{ background: '#DBEAFE', color: '#1D4ED8' }}>
                      {chip.label}
                      <button type="button" onClick={() => setMailChips(p => p.filter((_, j) => j !== i))}
                        className="ml-0.5 hover:opacity-70">
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                  {mailChips.length === 0 && (
                    <span className="text-xs italic" style={{ color: '#9CA3AF' }}>
                      Ajoutez un contact ci-dessous ou saisissez une adresse manuellement
                    </span>
                  )}
                </div>
                {/* Contacts disponibles (Achats) */}
                {contacts.filter(c => c.email && !mailChips.find(ch => ch.email === c.email)).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className="text-xs" style={{ color: '#9CA3AF' }}>Ajouter :</span>
                    {contacts.filter(c => c.email && !mailChips.find(ch => ch.email === c.email)).map(c => {
                      const label = `${c.prenom || ''} ${c.nom || ''}`.trim() || c.email;
                      return (
                        <button key={c.personne_id || c.contact_id} type="button"
                          onClick={() => setMailChips(p => [...p, { email: c.email, label }])}
                          className="text-xs px-2 py-0.5 rounded-full border hover:bg-blue-50 transition-colors"
                          style={{ borderColor: '#BFDBFE', color: '#1D4ED8' }}>
                          + {label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Saisie manuelle */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                  Autres adresses <span style={{ color: '#9CA3AF' }}>(séparées par une virgule)</span>
                </label>
                <input type="text" value={mailManualInput}
                  onChange={e => setMailManualInput(e.target.value)}
                  placeholder="email1@exemple.fr, email2@exemple.fr"
                  data-testid="mail-achat-manuel"
                  className="w-full text-sm px-3 py-2 rounded-lg border focus:outline-none focus:ring-1"
                  style={{ borderColor: '#E6E6E1' }} />
              </div>

              {/* Sujet */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>Objet</label>
                <input type="text" value={mailSujet}
                  onChange={e => setMailSujet(e.target.value)}
                  data-testid="mail-achat-sujet"
                  className="w-full text-sm px-3 py-2 rounded-lg border focus:outline-none focus:ring-1"
                  style={{ borderColor: '#E6E6E1' }} />
              </div>

              {/* Corps */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>Message</label>
                <textarea value={mailCorps}
                  onChange={e => setMailCorps(e.target.value)}
                  rows={5}
                  data-testid="mail-achat-corps"
                  className="w-full text-sm px-3 py-2 rounded-lg border focus:outline-none resize-none"
                  style={{ borderColor: '#E6E6E1' }} />
              </div>

              <div className="rounded-lg px-3 py-2 text-xs" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                Le PDF sera automatiquement généré et ajouté en pièce jointe.
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: '#E6E6E1' }}>
              <button onClick={() => setShowMailModal(false)}
                className="px-4 py-2 rounded-xl text-sm border" style={{ borderColor: '#E6E6E1' }}>
                Annuler
              </button>
              <button onClick={handleSendMail} disabled={sendingMail}
                data-testid="mail-achat-envoyer-btn"
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: '#1D4ED8' }}>
                {sendingMail ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {sendingMail ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SidePanel>
      {/* FactureConfirmModal — Radix Dialog imbriqué dans le tree du Sheet pour éviter le blocage inert */}
      <FactureConfirmModal
        open={!!localFichier}
        sending={localSending}
        onClose={() => setLocalFichier(null)}
        onConfirm={handleLocalSend}
      />

      {/* ── Popup avertissement Réceptionner ── (Portal pour éviter le blocage inert du Sheet Radix) */}
      {showReceptionWarning && createPortal(
        <div
          className="fixed inset-0 z-[500] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={() => setShowReceptionWarning(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#FEF9C3' }}>
                  <PackageCheck size={18} style={{ color: '#CA8A04' }} />
                </div>
                <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Réceptionner la commande</p>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                La réception d'une commande se fait depuis l'onglet <strong>Factures</strong>.
                Enregistrez d'abord la facture fournisseur, puis créez le bon de réception depuis celle-ci.
              </p>
            </div>
            <div className="flex gap-2 px-5 pb-5">
              <button
                onClick={() => setShowReceptionWarning(false)}
                className="flex-1 px-4 py-2 rounded-xl border text-sm font-medium"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                Fermer
              </button>
              <button
                onClick={() => { setShowReceptionWarning(false); setActiveTab('factures'); loadFactures(editAchat.achat_id); }}
                data-testid="btn-receptionner-goto-factures"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
                style={{ background: '#2563EB' }}>
                <Receipt size={14} /> Aller aux Factures
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Modale Virement SEPA ─────────────────────────────────────────────────────

function ModalVirementSepa({ achats, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);

  // Libellé agrégé : numéros + refs fournisseur
  const defaultLibelle = achats
    .map(a => [a.numero, a.ref_fournisseur].filter(Boolean).join(' / '))
    .join(' — ');
  const totalTtc = achats.reduce((s, a) => s + parseFloat(a.total_ttc || 0), 0);
  const fournisseur = { id: achats[0].fournisseur_id, nom: achats[0].fournisseur_nom };

  const [montant, setMontant]       = useState(totalTtc.toFixed(2));
  const [dateVir, setDateVir]       = useState(today);
  const [libelle, setLibelle]       = useState(defaultLibelle);
  const [ibanBenef, setIbanBenef]   = useState('');
  const [ibanEmet, setIbanEmet]     = useState('');
  const [nomEmet, setNomEmet]       = useState('');
  const [saving, setSaving]         = useState(false);
  const [loadingIbans, setLoadingIbans] = useState(true);
  const [doublons, setDoublons]     = useState([]); // [{achat_id, numero, acompte_numero}]

  useEffect(() => {
    let active = true;
    const achatIds = achats.map(a => a.achat_id);
    Promise.all([
      api.getEntreprise(fournisseur.id).catch(() => null),
      api.getAcompteIbanEmetteur().catch(() => null),
      // Vérifier les doublons : chercher les acomptes existants liés à ces commandes
      api.getAcomptes({ fournisseur_id: fournisseur.id }).catch(() => null),
    ]).then(([entRes, emetRes, acpRes]) => {
      if (!active) return;
      const ibans = entRes?.data?.ibans || [];
      setIbanBenef(ibans[0]?.iban || '');
      setIbanEmet(emetRes?.data?.iban || '');
      setNomEmet(emetRes?.data?.nom || '');

      // Détection des doublons
      const existingAcomptes = acpRes?.data || [];
      const found = [];
      for (const acp of existingAcomptes) {
        const linkedIds = (acp.achat_ids || []).map(x => x.achat_id);
        for (const aid of achatIds) {
          if (linkedIds.includes(aid)) {
            const a = achats.find(x => x.achat_id === aid);
            found.push({ achat_id: aid, numero: a?.numero || aid, acompte_numero: acp.numero });
          }
        }
      }
      setDoublons(found);
      setLoadingIbans(false);
    });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fournisseur.id]);

  const fmtIban = (v) => (v || '').replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();

  const handleSubmit = async () => {
    if (!montant || parseFloat(montant) <= 0) { toast.error('Montant invalide'); return; }
    if (!ibanBenef.replace(/\s/g, '')) { toast.error('IBAN bénéficiaire (fournisseur) manquant'); return; }
    if (!ibanEmet.replace(/\s/g, '')) { toast.error('IBAN émetteur manquant — configurez l\'IBAN de votre société'); return; }
    if (doublons.length > 0) {
      toast.error(`Doublon détecté : ${doublons.map(d => `${d.numero} → ${d.acompte_numero}`).join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const r = await api.createAcompte({
        fournisseur_id:    fournisseur.id,
        fournisseur_nom:   fournisseur.nom,
        montant_ttc:       parseFloat(montant),
        iban_beneficiaire: ibanBenef.replace(/\s/g, ''),
        iban_emetteur:     ibanEmet.replace(/\s/g, ''),
        nom_emetteur:      nomEmet,
        date_virement:     dateVir,
        libelle_virement:  libelle,
        achat_ids:         achats.map(a => ({ achat_id: a.achat_id, numero: a.numero })),
      });
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally {
      setSaving(false);
    }
  };

  const inpSt = {
    border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px',
    fontSize: 13, color: 'var(--text)', background: 'var(--surface)', outline: 'none', width: '100%',
  };

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.45)',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto',
        padding: '26px 26px 22px',
      }}>
        {/* En-tête */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <CreditCard size={18} style={{ color: 'var(--primary-solid)' }} />
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Envoyer en paiement</h2>
          </div>
          <button onClick={onClose} style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={17} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Alertes doublons */}
          {doublons.length > 0 && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', gap: 8 }}>
              <span style={{ fontSize: 15, flexShrink: 0 }}>⚠</span>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#DC2626', marginBottom: 3 }}>Virement déjà existant</p>
                {doublons.map(d => (
                  <p key={d.achat_id} style={{ fontSize: 12, color: '#DC2626' }}>
                    {d.numero} → déjà lié à <strong>{d.acompte_numero}</strong>
                  </p>
                ))}
                <p style={{ fontSize: 11, color: '#B91C1C', marginTop: 4 }}>Supprimez ou soldez le virement existant avant d'en créer un nouveau.</p>
              </div>
            </div>
          )}

          {/* Fournisseur */}
          <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--bg-muted,#f9fafb)', border: '1px solid var(--border)' }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>Fournisseur</p>
            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{fournisseur.nom}</p>
          </div>

          {/* Commandes sélectionnées */}
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
              Commandes ({achats.length})
            </p>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {achats.map((a, i) => (
                <div key={a.achat_id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px',
                  borderTop: i > 0 ? '1px solid var(--border-light)' : 'none',
                  background: 'white',
                }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--primary-solid)', flex: '0 0 auto' }}>{a.numero}</span>
                  {a.ref_fournisseur && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>Réf. fourn. : {a.ref_fournisseur}</span>
                  )}
                  <span style={{ fontSize: 13, fontWeight: 600, flex: '0 0 auto' }}>
                    {parseFloat(a.total_ttc || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Montant + Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                Montant TTC (€) <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input type="number" step="0.01" min="0"
                value={montant} onChange={e => setMontant(e.target.value)}
                style={inpSt} data-testid="virement-montant" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                Date de virement
              </label>
              <input type="date" value={dateVir} onChange={e => setDateVir(e.target.value)}
                style={inpSt} data-testid="virement-date" />
            </div>
          </div>

          {/* Libellé */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Libellé du virement
            </label>
            <input type="text" value={libelle} onChange={e => setLibelle(e.target.value)}
              maxLength={140} style={inpSt} data-testid="virement-libelle" />
          </div>

          {/* IBANs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                IBAN bénéficiaire {!loadingIbans && !ibanBenef && <span style={{ color: '#DC2626' }}>*</span>}
              </label>
              <div style={{
                ...inpSt, fontFamily: 'monospace', fontSize: 11,
                background: 'var(--bg-muted,#f9fafb)',
                color: (!loadingIbans && !ibanBenef) ? '#DC2626' : 'var(--text)',
                borderColor: (!loadingIbans && !ibanBenef) ? '#FCA5A5' : undefined,
                display: 'flex', alignItems: 'center',
              }} data-testid="virement-iban-benef">
                {loadingIbans ? 'Chargement…' : ibanBenef ? fmtIban(ibanBenef) : 'Non configuré'}
              </div>
              {!loadingIbans && !ibanBenef && (
                <p style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>Configurez l'IBAN dans la fiche du fournisseur</p>
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                IBAN émetteur {!loadingIbans && !ibanEmet && <span style={{ color: '#DC2626' }}>*</span>}
              </label>
              <div style={{
                ...inpSt, fontFamily: 'monospace', fontSize: 11,
                background: 'var(--bg-muted,#f9fafb)',
                color: (!loadingIbans && !ibanEmet) ? '#DC2626' : 'var(--text-muted)',
                borderColor: (!loadingIbans && !ibanEmet) ? '#FCA5A5' : undefined,
                display: 'flex', alignItems: 'center',
              }}>
                {loadingIbans ? 'Chargement…' : ibanEmet ? fmtIban(ibanEmet) : 'Non configuré'}
              </div>
              {!loadingIbans && !ibanEmet && (
                <p style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>
                  Non détecté — liez une fiche entreprise à votre agence dans <strong>Paramètres → Sociétés & Agences</strong>
                </p>
              )}
              {nomEmet && <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{nomEmet}</p>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
          <button onClick={onClose} style={{
            padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)',
            fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'white', cursor: 'pointer',
          }}>Annuler</button>
          {(() => {
            const blocked = loadingIbans || doublons.length > 0 || !ibanBenef.replace(/\s/g,'') || !ibanEmet.replace(/\s/g,'');
            return (
              <button onClick={handleSubmit} disabled={saving || blocked}
                data-testid="virement-submit-btn"
                title={blocked && !loadingIbans ? (doublons.length > 0 ? 'Doublon détecté' : 'IBAN(s) manquant(s)') : undefined}
                style={{
                  padding: '7px 20px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
                  color: 'white', background: blocked ? '#9CA3AF' : 'var(--primary-gradient)',
                  cursor: (saving || blocked) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />}
                {saving ? 'Création…' : 'Créer le virement'}
              </button>
            );
          })()}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function Achats() {
  const navigate = useNavigate();
  const { activeSociete, activeAgences, societes } = useSociete();
  const isReadOnly = !activeSociete;

  // Carte agence_id → provider_comptable (routing Odoo/Pennylane)
  const agenceProviderMap = useMemo(() => {
    const map = {};
    for (const soc of (societes || [])) {
      for (const ag of (soc.agences || [])) {
        map[ag.agence_id] = soc.provider_comptable || 'odoo';
      }
    }
    return map;
  }, [societes]);
  const [searchParams, setSearchParams] = useSearchParams();
  const filterIds = searchParams.get('filter_ids')?.split(',').filter(Boolean) || null;
  const filterSourceLabel = searchParams.get('source_label') || '';

  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStades, setFilterStades] = useState([]);
  const [filterFournisseurs, setFilterFournisseurs] = useState([]);
  const [filterReception, setFilterReception]     = useState('');
  const [filterFacturation, setFilterFacturation] = useState('');
  const [search, setSearch] = useState('');

  // Colonnes visibles
  const [visibleCols, setVisibleCols] = useState(loadAchatCols);
  const [showColMenu, setShowColMenu] = useState(false);
  const colMenuRef = useRef(null);
  const colBtnRef = useRef(null);
  const colDropdownRef = useRef(null);
  const [colDropdownPos, setColDropdownPos] = useState({ top: 0, right: 0 });

  // Sélection de lignes
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modal virement SEPA
  const [showVirementModal, setShowVirementModal] = useState(false);

  const openVirementModal = useCallback(() => {
    // Ne garder que les achats au stade "commande"
    const commandeAchats = (filterIds ? achats.filter(a => filterIds.includes(a.achat_id)) : achats)
      .filter(a => selectedIds.has(a.achat_id) && a.stade === 'commande');

    if (commandeAchats.length === 0) {
      toast.error('Sélectionnez au moins une commande au stade "Commande"');
      return;
    }
    // Vérifier même fournisseur
    const fournIds = [...new Set(commandeAchats.map(a => a.fournisseur_id))];
    if (fournIds.length > 1) {
      toast.error('Toutes les commandes sélectionnées doivent appartenir au même fournisseur');
      return;
    }
    setShowVirementModal(true);
  }, [selectedIds, achats, filterIds]);

  // Achats sélectionnés au stade commande (pour la modale)
  const virementAchats = useMemo(() => {
    const base = filterIds ? achats.filter(a => filterIds.includes(a.achat_id)) : achats;
    return base.filter(a => selectedIds.has(a.achat_id) && a.stade === 'commande');
  }, [selectedIds, achats, filterIds]);

  // Données de référence
  const [fournisseurs, setFournisseurs] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [articles, setArticles] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [sousFamilles, setSousFamilles] = useState([]);
  const [tvaRates, setTvaRates] = useState([]);
  const [uniteDecimals, setUniteDecimals] = useState({});

  useEffect(() => {
    api.getUnites().then(r => {
      const map = {};
      (r.data || []).forEach(u => { if (u.symbole) map[u.symbole] = u.nb_decimales ?? 0; });
      setUniteDecimals(map);
    }).catch(() => {});
  }, []);

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [editAchat, setEditAchat] = useState(null);
  const achatProvider = agenceProviderMap[editAchat?.agence_id] || 'odoo';
  const [editAchatKey, setEditAchatKey] = useState(0); // incrémenté quand les données complètes arrivent → force remount AchatForm
  const [showBR, setShowBR] = useState(false); // kept for safety — not used

  const _achatsCacheKey = `achats-${activeSociete?.societe_id || 'all'}-${activeAgences.map(a => a.agence_id).join(',')}`;
  const _achatsRefCacheKey = 'achats-ref';

  const loadAchats = useCallback(async () => {
    // Stale-While-Revalidate : afficher les données en cache immédiatement (même périmées),
    // et recharger en arrière-plan sans spinner visible.
    const { data: cachedData, isStale } = pageCache.getOrStale(_achatsCacheKey);
    if (cachedData) {
      setAchats(cachedData);
      setLoading(false);
      if (!isStale) return; // cache frais, pas de rechargement
      // cache périmé → refresh silencieux (pas de spinner)
    } else {
      setLoading(true);
    }
    try {
      const r = await api.getAchats();
      const achatsData = r.data || [];
      setAchats(achatsData);
      pageCache.set(_achatsCacheKey, achatsData);
    } catch { if (!cachedData) toast.error('Erreur chargement des achats'); }
    finally { if (!cachedData) setLoading(false); }
  }, [_achatsCacheKey]);

  useEffect(() => { loadAchats(); }, [loadAchats]);

  // ── Ouverture directe via URL ?open_achat=ACHAT_ID ───────────────────────────
  useEffect(() => {
    const achatId = searchParams.get('open_achat');
    if (!achatId) return;
    const found = achats.find(a => a.achat_id === achatId);
    if (found) { openEdit(found); setSearchParams({}, { replace: true }); return; }
    api.getAchat(achatId)
      .then(r => { if (r.data) { openEdit(r.data); setSearchParams({}, { replace: true }); } })
      .catch(() => {});
  }, [searchParams, achats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les données de référence une seule fois (avec cache) — au premier ouverture du formulaire
  const loadRefData = useCallback(async () => {
    const cached = pageCache.get(_achatsRefCacheKey);
    if (cached) {
      setFournisseurs(cached.fournisseurs);
      setStocks(cached.stocks);
      setArticles(cached.articles);
      setFamilles(cached.familles);
      setSousFamilles(cached.sousFamilles);
      setTvaRates(cached.tvaRates);
      return;
    }
    Promise.all([
      api.getEntreprises({ type: 'fournisseur', exclude_gele: true }),
      api.getStocks(),
      api.getArticles({ slim: true }),
      api.getFamilles(),
      api.getSousFamilles(),
      api.getTvaRates(),
    ]).then(([f, s, a, fam, sf, tva]) => {
      const articlesData = Array.isArray(a.data) ? a.data : (a.data?.items || a.data?.data || []);
      const refData = {
        fournisseurs: f.data || [],
        stocks: s.data || [],
        articles: articlesData,
        familles: fam.data || [],
        sousFamilles: sf.data || [],
        tvaRates: tva.data || [],
      };
      setFournisseurs(refData.fournisseurs);
      setStocks(refData.stocks);
      setArticles(refData.articles);
      setFamilles(refData.familles);
      setSousFamilles(refData.sousFamilles);
      setTvaRates(refData.tvaRates);
      pageCache.set(_achatsRefCacheKey, refData);
    }).catch(() => {});
  }, [_achatsRefCacheKey]);

  const toggleCol = (key) => {
    setVisibleCols(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(ACHAT_COLS_KEY, JSON.stringify(next));
      return next;
    });
  };
  const show = (k) => visibleCols[k] !== false;

  // Calcul dynamique des % de largeur selon les colonnes visibles
  const colWidths = useMemo(() => {
    const active = ['_checkbox',
      ...ACHAT_COLUMN_DEFS.filter(c => visibleCols[c.key] !== false).map(c => c.key),
      '_suivi_par', '_spacer',
    ];
    const total = active.reduce((s, k) => s + (COL_WEIGHTS[k] || 0), 0);
    return Object.fromEntries(active.map(k => [k, `${((COL_WEIGHTS[k] || 0) / total * 100).toFixed(2)}%`]));
  }, [visibleCols]);

  const toggleRow = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllRows = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(a => a.achat_id)));
  };
  const openColMenu = () => {
    if (colBtnRef.current) {
      const rect = colBtnRef.current.getBoundingClientRect();
      setColDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setShowColMenu(v => !v);
  };

  const hasActiveFilters = filterStades.length > 0 || filterFournisseurs.length > 0 || filterReception || filterFacturation;

  // ── Tri colonnes ─────────────────────────────────────────────────────────────
  const [sortConfig, setSortConfig] = useState({ key: 'numero', dir: 'desc' });
  const handleSort = (key) => setSortConfig(prev =>
    prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }
  );
  const getSortValue = (a, key) => {
    switch (key) {
      case 'numero':      return a.numero || '';
      case 'fournisseur': return a.fournisseur_nom || '';
      case 'titre':       return a.titre || '';
      case 'date':        return a.date_commande || '';
      case 'livraison':   return a.date_livraison_prevue || '';
      case 'nb_lignes':   return Array.isArray(a.lignes) ? a.lignes.filter(l => !l.type_ligne || l.type_ligne === 'article').length : 0;
      case 'total_ttc':   return a.total_ttc || 0;
      case 'stade':       return a.stade || '';
      case 'reception':   return a.statut_reception || '';
      case 'facturation': return a.statut_facturation_achat || '';
      case 'suivi_par':   return (a.created_by_nom || '').split(' ')[0] || '';
      default:            return '';
    }
  };

  // Filtrage + tri frontend
  const filtered = useMemo(() => {
    let list = achats;
    if (filterStades.length > 0) list = list.filter(a => filterStades.includes(a.stade));
    if (filterFournisseurs.length > 0) list = list.filter(a => filterFournisseurs.includes(a.fournisseur_id));
    if (filterReception)   list = list.filter(a => a.statut_reception === filterReception);
    if (filterFacturation) list = list.filter(a => a.statut_facturation_achat === filterFacturation);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        (a.numero || '').toLowerCase().includes(q) ||
        (a.fournisseur_nom || '').toLowerCase().includes(q) ||
        (a.titre || '').toLowerCase().includes(q)
      );
    }
    if (sortConfig.key) {
      list = [...list].sort((a, b) => {
        const va = getSortValue(a, sortConfig.key);
        const vb = getSortValue(b, sortConfig.key);
        const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), 'fr', { numeric: true });
        return sortConfig.dir === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  }, [achats, filterStades, filterFournisseurs, filterReception, filterFacturation, search, sortConfig]);

  // Comptages par stade pour les cards (calculé localement depuis la liste complète)
  const counts = useMemo(() => Object.fromEntries(
    Object.keys(STADES_CARDS).map(k => [k, achats.filter(a => a.stade === k).length])
  ), [achats]);

  const handleStadeCardClick = (stade) => {
    if (filterStades.length === 1 && filterStades[0] === stade) {
      setFilterStades([]); // déselectionner si déjà actif
    } else {
      setFilterStades([stade]); // sélection unique
    }
  };

  // Fermer le menu colonnes en cliquant dehors
  useEffect(() => {
    const h = (e) => {
      if (
        colBtnRef.current && !colBtnRef.current.contains(e.target) &&
        colDropdownRef.current && !colDropdownRef.current.contains(e.target)
      ) setShowColMenu(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const openCreate = () => { setEditAchat(null); setShowForm(true); loadRefData(); };
  const openEdit = async (a) => {
    loadRefData(); // données de référence en parallèle
    // Ouvrir immédiatement le panel avec les données de la liste (optimiste)
    setEditAchat(a);
    setShowForm(true);
    // Toujours refetcher les données complètes (lignes + titre + tous les champs)
    // La liste ne retourne pas les lignes ni ne garantit la fraîcheur de tous les champs.
    try {
      const res = await api.getAchat(a.achat_id);
      setEditAchat(res.data);
      setEditAchatKey(k => k + 1);
    } catch { /* on garde les données de la liste — non bloquant */ }
  };
  const openBR = (_a) => {};  // navigates via button, kept for reference

  const handlePanelChangeStade = async (stade) => {
    try {
      const res = await api.changeStadeAchat(editAchat.achat_id, { stade });
      setAchats(prev => prev.map(x => x.achat_id === editAchat.achat_id ? res.data : x));
      toast.success(`Bon passé en "${stadeInfo(stade).label}"`);
      if (res.data?.pl_warning) toast.warning(res.data.pl_warning, { duration: 10000 });
      if (stade === 'devis_envoye' || stade === 'annule') {
        setEditAchat(res.data); // Garder le formulaire ouvert, mettre à jour editAchat
      } else {
        setShowForm(false);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Transition non autorisée'); }
  };

  const handlePanelDelete = async () => {
    if (!window.confirm(`Supprimer le bon "${editAchat.numero}" ?`)) return;
    try {
      await api.deleteAchat(editAchat.achat_id);
      setAchats(prev => prev.filter(x => x.achat_id !== editAchat.achat_id));
      toast.success('Bon d\'achat supprimé');
      setShowForm(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handlePanelOpenBR = () => {
    if (editAchat?.achat_id) navigate(`/bons-reception?achat_id=${editAchat.achat_id}`);
    setShowForm(false);
  };

  const handleSaved = (achat) => {
    setAchats(prev => {
      const idx = prev.findIndex(a => a.achat_id === achat.achat_id);
      if (idx >= 0) return prev.map((a, i) => i === idx ? achat : a);
      return [achat, ...prev];
    });
    setShowForm(false);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const formatMoney = (v) => v != null ? Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €' : '—';

  // ── Compteurs entonnoir Facturation + Réception (comme /ventes) ─────────────
  const factCounts = useMemo(() => {
    let base = achats;
    if (filterStades.length > 0)       base = base.filter(a => filterStades.includes(a.stade));
    if (filterFournisseurs.length > 0)  base = base.filter(a => filterFournisseurs.includes(a.fournisseur_id));
    const c = {};
    Object.keys(FACTURATION_ACHAT_CFG).forEach(k => { c[k] = base.filter(a => a.statut_facturation_achat === k).length; });
    return c;
  }, [achats, filterStades, filterFournisseurs]);

  const receptCounts = useMemo(() => {
    let base = achats;
    if (filterStades.length > 0)       base = base.filter(a => filterStades.includes(a.stade));
    if (filterFournisseurs.length > 0)  base = base.filter(a => filterFournisseurs.includes(a.fournisseur_id));
    if (filterFacturation)              base = base.filter(a => a.statut_facturation_achat === filterFacturation);
    const c = {};
    Object.keys(RECEPTION_CFG).forEach(k => { c[k] = base.filter(a => a.statut_reception === k).length; });
    return c;
  }, [achats, filterStades, filterFournisseurs, filterFacturation]);

  return (
    <div className="space-y-5">
      {isReadOnly && <ReadOnlyBanner />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
            Bons d'achat
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Gérez vos commandes fournisseurs et réceptions
          </p>
        </div>
        {!isReadOnly && (
          <button onClick={openCreate} data-testid="new-achat-btn"
            disabled={activeAgences.length !== 1}
            title={activeAgences.length !== 1 ? 'Sélectionnez une seule agence pour créer un bon d\'achat' : ''}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'var(--primary-gradient)' }}>
            <Plus size={16} /> Nouveau bon d'achat
          </button>
        )}
      </div>

      {/* Stats par stade */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <button onClick={() => setFilterStades([])}
          data-testid="achat-stat-tous"
          className="rounded-xl p-4 text-left border transition-all hover:shadow-md"
          style={{
            background: filterStades.length === 0 ? '#F8FAFC' : 'var(--surface)',
            borderColor: filterStades.length === 0 ? '#94A3B8' : 'var(--border)',
          }}>
          <div className="text-2xl font-bold" style={{ color: '#475569' }}>{achats.length}</div>
          <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-secondary)' }}>Tous</div>
        </button>
        {Object.entries(STADES_CARDS).map(([key, s]) => {
          const active = filterStades.length === 1 && filterStades[0] === key;
          return (
            <button key={key} onClick={() => handleStadeCardClick(key)}
              data-testid={`achat-stat-${key}`}
              className="rounded-xl p-4 text-left border transition-all hover:shadow-md"
              style={{
                background: active ? s.bg : 'var(--surface)',
                borderColor: active ? s.border : 'var(--border)',
              }}>
              <div className="text-2xl font-bold" style={{ color: s.color }}>{counts[key] || 0}</div>
              <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-secondary)' }}>{s.label}</div>
            </button>
          );
        })}
      </div>

      {/* Filtres Facturation + Réception — entonnoir identique à /ventes */}
      <div className="grid grid-cols-2 gap-3">
        {/* Bloc Facturation */}
        <div className="flex items-center justify-center gap-3 py-2.5 px-4 rounded-xl"
          style={{ background: 'var(--surface)' }}>
          <span className="text-xs font-semibold uppercase tracking-widest shrink-0"
            style={{ color: 'var(--text-muted)' }}>Facturation</span>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(FACTURATION_ACHAT_CFG).map(([key, s]) => {
              const active = filterFacturation === key;
              return (
                <button key={key}
                  data-testid={`achat-facturation-${key}`}
                  onClick={() => setFilterFacturation(active ? '' : key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all"
                  style={{
                    background: active ? s.bg : 'var(--background)',
                    borderColor: active ? s.color : 'var(--border)',
                    color: active ? s.color : 'var(--text-muted)',
                    boxShadow: active ? `0 0 0 2px ${s.border}` : 'none',
                    fontWeight: active ? 700 : 500,
                  }}>
                  <span style={{ color: s.color, fontWeight: 700 }}>{factCounts[key] ?? 0}</span>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bloc Réception */}
        <div className="flex items-center justify-center gap-3 py-2.5 px-4 rounded-xl"
          style={{ background: 'var(--surface)' }}>
          <span className="text-xs font-semibold uppercase tracking-widest shrink-0"
            style={{ color: 'var(--text-muted)' }}>Réception</span>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(RECEPTION_CFG).map(([key, s]) => {
              const active = filterReception === key;
              return (
                <button key={key}
                  data-testid={`achat-reception-${key}`}
                  onClick={() => setFilterReception(active ? '' : key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all"
                  style={{
                    background: active ? s.bg : 'var(--background)',
                    borderColor: active ? s.color : 'var(--border)',
                    color: active ? s.color : 'var(--text-muted)',
                    boxShadow: active ? `0 0 0 2px ${s.border}` : 'none',
                    fontWeight: active ? 700 : 500,
                  }}>
                  <span style={{ color: s.color, fontWeight: 700 }}>{receptCounts[key] ?? 0}</span>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filtres + sélection sur la même ligne */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Numéro, fournisseur..."
            data-testid="achats-search"
            className="w-full pl-9 pr-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }} />
        </div>
        <FilterDropdown testId="filter-stade" label="Stade"
          selected={filterStades} onChange={setFilterStades}
          options={STADES.map(s => ({ value: s.value, label: s.label }))} />
        {fournisseurs.length > 0 && (
          <FilterDropdown testId="filter-fournisseur" label="Fournisseur"
            selected={filterFournisseurs} onChange={setFilterFournisseurs}
            options={fournisseurs.map(f => ({ value: f.entreprise_id, label: f.nom }))} />
        )}
        {hasActiveFilters && (
          <button onClick={() => { setFilterStades([]); setFilterFournisseurs([]); setFilterReception(''); setFilterFacturation(''); }}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium"
            style={{ color: 'var(--text-muted)' }}>
            <X size={12} /> Réinitialiser
          </button>
        )}

        {/* Sélection — poussée à droite */}
        {selectedIds.size > 0 && (
          <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-xl border"
            style={{ background: 'var(--primary-50)', borderColor: 'var(--primary-solid)' }}>
            <span className="text-xs font-semibold" style={{ color: 'var(--primary-solid)' }}>
              {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
            </span>
            <button onClick={openVirementModal}
              data-testid="envoyer-paiement-btn"
              className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border transition-colors"
              style={{ borderColor: 'var(--primary-solid)', color: 'white', background: 'var(--primary-solid)' }}>
              <CreditCard size={11} /> Envoyer en paiement
            </button>
            <button onClick={() => setSelectedIds(new Set())}
              className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border transition-colors"
              style={{ borderColor: 'var(--primary-solid)', color: 'var(--primary-solid)', background: 'white' }}>
              <X size={10} /> Tout effacer
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)', background: 'white' }}>
        {loading ? (
          <div className="flex items-center justify-center py-12 gap-3">
            <div className="animate-spin w-5 h-5 border-2 rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Chargement...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag size={40} className="mx-auto mb-3 opacity-20" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>Aucun bon d'achat</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
              {hasActiveFilters || search ? 'Aucun résultat pour ces critères' : 'Créez votre premier bon d\'achat'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }} data-testid="achats-table">
            <colgroup>
              <col style={{ width: colWidths._checkbox }} />
              {show('numero')      && <col style={{ width: colWidths.numero }} />}
              {show('fournisseur') && <col style={{ width: colWidths.fournisseur }} />}
              {show('titre')       && <col style={{ width: colWidths.titre }} />}
              {show('date')        && <col style={{ width: colWidths.date }} />}
              {show('livraison')   && <col style={{ width: colWidths.livraison }} />}
              {show('nb_lignes')   && <col style={{ width: colWidths.nb_lignes }} />}
              {show('total_ttc')   && <col style={{ width: colWidths.total_ttc }} />}
              {show('stade')       && <col style={{ width: colWidths.stade }} />}
              {show('facturation') && <col style={{ width: colWidths.facturation }} />}
              {show('reception')   && <col style={{ width: colWidths.reception }} />}
              <col style={{ width: colWidths._suivi_par }} />
              <col style={{ width: colWidths._spacer }} />
            </colgroup>
            <thead style={{ borderBottom: `1px solid var(--border)`, background: 'var(--bg-subtle)' }}>
              <tr>
                <th className="px-3 py-2.5" style={{ width: 36 }}>
                  <input type="checkbox"
                    checked={filtered.length > 0 && selectedIds.size === filtered.length}
                    onChange={toggleAllRows}
                    className="w-3.5 h-3.5 rounded"
                    style={{ accentColor: 'var(--primary-solid)' }} />
                </th>
                {show('numero')      && <th onClick={() => handleSort('numero')} className="text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center gap-1">Numéro {sortConfig.key === 'numero' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                {show('fournisseur') && <th onClick={() => handleSort('fournisseur')} className="text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center gap-1">Fournisseur {sortConfig.key === 'fournisseur' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                {show('titre')       && <th onClick={() => handleSort('titre')} className="text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center gap-1">Titre {sortConfig.key === 'titre' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                {show('date')        && <th onClick={() => handleSort('date')} className="text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center gap-1">Date {sortConfig.key === 'date' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                {show('livraison')   && <th onClick={() => handleSort('livraison')} className="text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center gap-1">Livraison {sortConfig.key === 'livraison' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                {show('nb_lignes')   && <th onClick={() => handleSort('nb_lignes')} className="text-center px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center gap-1">Articles {sortConfig.key === 'nb_lignes' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                {show('total_ttc')   && <th onClick={() => handleSort('total_ttc')} className="text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center gap-1">Total TTC {sortConfig.key === 'total_ttc' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                {show('stade')       && <th onClick={() => handleSort('stade')} className="text-center px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center justify-center gap-1">Statut {sortConfig.key === 'stade' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                {show('facturation') && <th onClick={() => handleSort('facturation')} className="text-center px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center justify-center gap-1">Facturation {sortConfig.key === 'facturation' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                {show('reception')   && <th onClick={() => handleSort('reception')} className="text-center px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center justify-center gap-1">Réception {sortConfig.key === 'reception' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>}
                <th onClick={() => handleSort('suivi_par')} className="text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                  <span className="inline-flex items-center gap-1">Suivi par {sortConfig.key === 'suivi_par' ? (sortConfig.dir === 'asc' ? <ChevronUp size={11}/> : <ChevronDown size={11}/>) : <ChevronUp size={11} style={{opacity:.25}}/>}</span>
                </th>
                <th className="px-2 py-2.5 text-center" style={{ width: 36 }}>
                  <div className="relative flex justify-center" ref={colMenuRef}>
                    <button onClick={openColMenu} ref={colBtnRef}
                      title="Choisir les colonnes" data-testid="col-picker-btn"
                      className="p-1 rounded hover:bg-gray-200 transition-colors"
                      style={{ color: 'var(--text-muted)' }}>
                      <SlidersHorizontal size={14} />
                    </button>
                    {showColMenu && (
                      <div ref={colDropdownRef}
                        className="fixed bg-white rounded-xl border shadow-xl z-50 py-2 min-w-44"
                        style={{ top: colDropdownPos.top, right: colDropdownPos.right, borderColor: 'var(--border)' }}>
                        <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Colonnes</p>
                        {ACHAT_COLUMN_DEFS.map(col => (
                          <label key={col.key} className={`flex items-center gap-2.5 px-3 py-1.5 ${col.locked ? 'opacity-50' : 'cursor-pointer hover:bg-gray-50'}`}>
                            <input type="checkbox"
                              checked={visibleCols[col.key] !== false}
                              disabled={col.locked}
                              onChange={() => !col.locked && toggleCol(col.key)}
                              className="w-3.5 h-3.5 rounded flex-shrink-0"
                              style={{ accentColor: 'var(--primary-solid)' }} />
                            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{col.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Bannière filtre smart button */}
              {filterIds && (
                <tr>
                  <td colSpan={20} style={{ padding: '8px 16px', background: '#EFF6FF', borderBottom: '1px solid #BFDBFE' }}>
                    <span className="text-xs font-medium" style={{ color: '#1D4ED8' }}>
                      Vue filtrée — {filterIds.length} commande{filterIds.length > 1 ? 's' : ''} liée{filterIds.length > 1 ? 's' : ''} à <strong>{filterSourceLabel}</strong>
                    </span>
                    <button type="button"
                      onClick={() => setSearchParams({}, { replace: true })}
                      className="ml-4 text-xs font-semibold px-3 py-0.5 rounded-full hover:bg-blue-100"
                      style={{ color: '#1D4ED8' }}>
                      × Effacer le filtre
                    </button>
                  </td>
                </tr>
              )}
              {(filterIds ? filtered.filter(a => filterIds.includes(a.achat_id)) : filtered).map((a) => (
                <tr key={a.achat_id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  style={{
                    borderBottom: '1px solid var(--border-light)',
                    background: selectedIds.has(a.achat_id) ? 'var(--primary-50)' : undefined,
                  }}
                  onClick={() => openEdit(a)}
                  data-testid={`achat-row-${a.achat_id}`}>
                  <td className="px-3 py-2.5" onClick={e => toggleRow(a.achat_id, e)}>
                    <input type="checkbox"
                      checked={selectedIds.has(a.achat_id)}
                      onChange={() => {}}
                      className="w-3.5 h-3.5 rounded cursor-pointer"
                      style={{ accentColor: 'var(--primary-solid)' }} />
                  </td>
                  {show('numero')      && <td className="px-3 py-2.5 font-semibold whitespace-nowrap overflow-hidden" style={{ color: 'var(--primary-solid)', textOverflow: 'ellipsis' }}>{a.numero}</td>}
                  {show('fournisseur') && <td className="px-3 py-2.5 font-medium overflow-hidden" style={{ color: 'var(--text)', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.fournisseur_nom || <span style={{ color: 'var(--text-placeholder)' }}>—</span>}</td>}
                  {show('titre')       && <td className="px-3 py-2.5 overflow-hidden" style={{ color: 'var(--text-secondary)', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.titre || <span style={{ color: 'var(--text-placeholder)' }}>—</span>}</td>}
                  {show('date')        && <td className="px-3 py-2.5" style={{ color: 'var(--text-muted)' }}>{formatDate(a.date_commande)}</td>}
                  {show('livraison')   && <td className="px-3 py-2.5" style={{ color: 'var(--text-muted)' }}>{a.date_livraison_prevue ? formatDate(a.date_livraison_prevue) : <span style={{ color: 'var(--text-placeholder)' }}>—</span>}</td>}
                  {show('nb_lignes')   && <td className="px-3 py-2.5 text-center" style={{ color: 'var(--text-secondary)' }}>{Array.isArray(a.lignes) ? a.lignes.filter(l => !l.type_ligne || l.type_ligne === 'article').length : 0}</td>}
                  {show('total_ttc')   && <td className="px-3 py-2.5 font-semibold" style={{ color: 'var(--text)' }}>{formatMoney(a.total_ttc)}</td>}
                  {show('stade')       && (
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex flex-wrap gap-1 justify-center">
                        <StageBadge stade={a.stade} />
                      </div>
                    </td>
                  )}
                  {show('facturation') && (
                    <td className="px-3 py-2.5 text-center"><FacturationAchatBadge statut={a.statut_facturation_achat} /></td>
                  )}
                  {show('reception')   && (
                    <td className="px-3 py-2.5 text-center"><ReceptionBadge statut={a.statut_reception} /></td>
                  )}
                  <td className="px-3 py-2.5 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                    {(a.created_by_nom || '').split(' ')[0] || '—'}
                  </td>
                  <td />
                </tr>
              ))}
            </tbody>

            {/* Footer — totaux */}
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-subtle)' }}>
                <td className="px-3 py-2.5" />
                {show('numero')      && <td className="px-3 py-2.5 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                  {selectedIds.size > 0
                    ? <>{selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</>
                    : <>{filtered.length} bon{filtered.length > 1 ? 's' : ''}</>
                  }
                </td>}
                {show('fournisseur') && <td />}
                {show('titre')       && <td />}
                {show('date')        && <td />}
                {show('livraison')   && <td />}
                {show('nb_lignes')   && <td />}
                {show('total_ttc')   && (() => {
                  const base = selectedIds.size > 0
                    ? filtered.filter(a => selectedIds.has(a.achat_id))
                    : filtered;
                  return (
                    <td className="px-3 py-2.5 font-bold text-sm" style={{ color: 'var(--primary-solid)' }}>
                      {formatMoney(base.reduce((s, a) => s + (a.total_ttc || 0), 0))}
                    </td>
                  );
                })()}
                {show('stade')       && <td />}
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Modals */}
      {showForm && (
        <AchatForm
          key={editAchat ? editAchat.achat_id + '_' + editAchatKey : 'new'}
          editAchat={editAchat}
          onClose={() => setShowForm(false)}
          onSaved={handleSaved}
          fournisseurs={fournisseurs}
          stocks={stocks}
          articles={articles}
          familles={familles}
          sousFamilles={sousFamilles}
          onChangeStade={handlePanelChangeStade}
          onDelete={handlePanelDelete}
          onOpenBR={handlePanelOpenBR}
          isReadOnly={isReadOnly}
          activeAgences={activeAgences}
          tvaRates={tvaRates}
          uniteDecimals={uniteDecimals}
          achatProvider={achatProvider}
        />
      )}
      {showVirementModal && virementAchats.length > 0 && (
        <ModalVirementSepa
          achats={virementAchats}
          onClose={() => setShowVirementModal(false)}
          onCreated={(acompte) => {
            setShowVirementModal(false);
            setSelectedIds(new Set());
            toast.success(`Virement ${acompte.numero} créé — consultez Achats > Acomptes/Virements`);
          }}
        />
      )}
    </div>
  );
}
